<template>
  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- ========== Left Sidebar Start ========== -->
    <NavigationBar/>
    <!-- Left Sidebar End -->

    <!-- ============================================================== -->
    <!-- Start right Content here -->
    <!-- ============================================================== -->
    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid">
          <!-- start page title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Member Details</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><router-link to="/members">Members</router-link></li>
                    <li class="breadcrumb-item active">{{ memberFullName }}</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Alerts -->
          <div v-if="alert.success" class="alert alert-success alert-dismissible fade show" role="alert">
            {{message}}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>
          <div v-if="alert.info" class="alert alert-primary alert-dismissible fade show" role="alert">
            {{message}}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>
          <div v-if="alert.failed" class="alert alert-danger alert-dismissible fade show" role="alert">
            {{message}}
            <button type="button" class="btn-close" @click="clearAlerts"></button>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading member details...</p>
          </div>

          <!-- Main Content -->
          <div v-else class="row">
            <div class="col-xxl-4 col-xl-6">
              <div class="card stretch stretch-full min-h-90vh-xl">
                <div class="card-header bg-white pb-3 border-bottom">
                  <div class="mb-4">

                    <div class="mb-4 me-3 position-relative">
                      <!-- Member Profile Photo -->
                      <div class="position-relative d-inline-block">
                        <!-- Clickable Photo -->
                        <img v-if="hasProfilePhoto"
                             :src="getPhotoUrl()"
                             class="rounded-circle border border-3 border-primary shadow-sm photo-clickable"
                             style="width: 80px; height: 80px; object-fit: cover; cursor: pointer;"
                             :alt="memberFullName"
                             @error="onImageError"
                             @click="viewPhotoFullScreen"
                             data-bs-toggle="tooltip"
                             data-bs-placement="top"
                             title="Click to view full photo">

                        <!-- Default Avatar (also clickable if no photo) -->
                        <div v-else
                             class="bg-primary rounded-circle d-flex align-items-center justify-content-center border border-3 border-primary shadow-sm photo-clickable"
                             style="width: 80px; height: 80px; cursor: pointer;"
                             @click="viewPhotoFullScreen"
                             data-bs-toggle="tooltip"
                             data-bs-placement="top"
                             title="Click to view photo">
                          <i class="mdi mdi-account-circle text-white" style="font-size: 3rem;"></i>
                        </div>

                        <!-- Photo Update Button -->
                        <button v-if="!isMemberDeleted && !isMemberSuspended"
                                @click="openPhotoModal"
                                class="btn btn-sm btn-primary rounded-circle position-absolute photo-update-btn"
                                style="bottom: -5px; right: -5px; width: 32px; height: 32px; padding: 0; z-index: 10;"
                                title="Update Photo"
                                @click.stop>
                          <i class="bx bx-camera" style="font-size: 14px;"></i>
                        </button>

                        <!-- Photo View Indicator -->
                        <div v-if="hasProfilePhoto"
                             class="position-absolute photo-view-indicator"
                             style="top: -5px; left: -5px;">
                          <i class="bx bx-expand text-primary bg-white rounded-circle p-1"
                             style="font-size: 12px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);"></i>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h5>{{ memberFullName }}
                        <span class="badge badge-pill font-size-11"
                              :class="memberStatusBadgeClass">
                          {{ getMemberStatusDisplay() }}
                        </span>
                      </h5>
                      <p class="text-muted mb-0">
                        <span>Member/Card ID: {{ memberInfo.cbhi_number || memberInfo.member_code }}</span>
                        <button type="button"
                                class="btn btn-info waves-effect"
                                @click="viewCard"
                                :disabled="isMemberDeleted || isMemberSuspended">
                          <i class="bx bx-id-card me-2"></i>
                          <span>{{ isCardIssued ? 'View Card' : 'Issue Card' }}</span>
                        </button>
                      </p>
                      <!-- Enhanced Plan Information -->
                      <div v-if="selectedPlan && !isMemberDeleted && !isMemberSuspended" class="plan-info-section mt-2">
                        <p class="text-info mb-1">
                          <i class="bx bx-shield-check me-1"></i>
                          Plan: <strong>{{ selectedPlan.plan_name }}</strong>
                          <span class="badge ms-1" :class="getPlanTypeBadgeClass(selectedPlan.plan_type)">
                            {{ selectedPlan.plan_type.toUpperCase() }}
                          </span>
                        </p>
                        <p class="text-muted small mb-0">
                          Coverage: ETB {{ formatCurrency(selectedPlan.annual_coverage_limit) }} annually |
                          ETB {{ formatCurrency(selectedPlan.per_visit_limit) }} per visit
                        </p>
                        <button class="btn btn-outline-primary btn-sm mt-2" @click="showPlanChangeModal">
                          <i class="bx bx-sync me-1"></i>
                          Change Plan
                        </button>
                      </div>
                      <!-- Suspension Information -->
                      <p v-if="isMemberSuspended && memberInfo.suspension_date" class="text-warning mb-0">
                        <i class="bx bx-pause-circle me-1"></i>
                        Suspended: {{ formatDate(memberInfo.suspension_date) }}
                      </p>
                      <!-- Deletion Information -->
                      <p v-if="isMemberDeleted && hasDeletionInfo" class="text-danger mb-0">
                        <i class="bx bx-trash me-1"></i>
                        Deleted: {{ formatDate(memberInfo.deletion_date) }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="card-body pb-3">
                  <!-- Suspended Member Alert -->
                  <div v-if="isMemberSuspended" class="alert alert-warning mb-4">
                    <div class="d-flex align-items-center">
                      <i class="bx bx-pause-circle fs-2 me-3"></i>
                      <div class="flex-grow-1">
                        <h6 class="alert-heading mb-1">
                          <i class="bx bx-lock me-2"></i>
                          This Member is {{ memberInfo.suspension_type === 'permanent' ? 'Permanently Archived' : 'Temporarily Suspended' }}
                        </h6>
                        <p class="mb-1" v-if="memberInfo.suspension_date">
                          <strong>Suspended on:</strong> {{ formatDate(memberInfo.suspension_date) }} <br>
                          <strong>Category:</strong> {{ formatSuspensionCategory(memberInfo.suspension_category) }} <br>
                          <strong>Reason:</strong> {{ memberInfo.suspension_reason }}
                          <span v-if="memberInfo.expected_reactivation_date">
                            <br><strong>Expected Reactivation:</strong> {{ formatDate(memberInfo.expected_reactivation_date) }}
                          </span>
                        </p>
                        <p class="mb-0">
                          <i class="bx bx-info-circle me-1"></i>
                          This account cannot process new claims or payments while suspended.
                          {{ memberInfo.suspension_type === 'temporary' ? 'Contact administration for reactivation.' : '' }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Deleted Member Alert -->
                  <div v-if="isMemberDeleted" class="alert alert-danger mb-4">
                    <div class="d-flex align-items-center">
                      <i class="bx bx-error-circle fs-2 me-3"></i>
                      <div class="flex-grow-1">
                        <h6 class="alert-heading mb-1">
                          <i class="bx bx-trash me-2"></i>
                          This Member Record Has Been Deleted
                        </h6>
                        <p class="mb-1" v-if="hasDeletionInfo">
                          <strong>Deleted on:</strong> {{ formatDate(memberInfo.deletion_date) }} <br>
                          <strong>Reason:</strong> {{ memberInfo.deletion_reason }}
                        </p>
                        <p class="mb-0">
                          <i class="bx bx-info-circle me-1"></i>
                          This record is archived and cannot be modified. All beneficiaries have been automatically removed.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div class="fw-normal fs-12 text-muted text-centermb-4 text-center text-capitalize">
                    <div class="row">
                      <div class="col-6 mb-4">
                        <div class="flex-fill py-3 px-4 rounded-1 border border-dashed border-gray-5">
                          <h6 class="fs-15 fw-bolder" :class="{ 'text-muted': isMemberDeleted || isMemberSuspended }">
                            ETB {{ (isMemberDeleted || isMemberSuspended) ? '0' : formatCurrency(annualPremium) }}
                          </h6>
                          <p class="fs-12 text-muted mb-0">
                            {{ getAnnualPremiumLabel() }}
                          </p>
                          <small v-if="!isMemberDeleted && !isMemberSuspended" class="text-info">
                            Plan: {{ selectedPlan?.plan_name || 'N/A' }}
                          </small>
                          <small v-if="isMemberDeleted" class="text-danger">
                            Record deleted
                          </small>
                          <small v-if="isMemberSuspended" class="text-warning">
                            Account suspended
                          </small>
                        </div>
                      </div>

                      <div class="col-6 mb-4">
                        <div class="flex-fill py-3 px-4 rounded-1 border border-dashed border-gray-5">
                          <h6 class="fs-15 fw-bolder" :class="{ 'text-muted': isMemberDeleted || isMemberSuspended }">
                            ETB {{ formatCurrency(totalPaidThisYear) }}
                          </h6>
                          <p class="fs-12 text-muted mb-0">
                            {{ getPaymentStatusLabel() }}
                          </p>
                          <small v-if="!isMemberDeleted && !isMemberSuspended" :class="paymentStatusClass">
                            {{ paymentStatusText }}
                          </small>
                          <small v-else class="text-muted">
                            No payments accepted
                          </small>
                        </div>
                      </div>

                      <div class="col-12 mb-4">
                        <div class="flex-fill py-3 px-4 rounded-1 d-none d-sm-block border border-dashed border-gray-5 w-100">
                          <h6 class="fs-15 fw-bolder" :class="{ 'text-muted': isMemberDeleted || isMemberSuspended }">
                            ETB {{ formatCurrency(totalClaimsAmount) }}
                          </h6>
                          <p class="fs-12 text-muted mb-0">
                            {{ getClaimsLabel() }}
                          </p>
                          <small v-if="!isMemberDeleted && !isMemberSuspended && selectedPlan" class="text-info">
                            Limit: ETB {{ formatCurrency(selectedPlan.annual_coverage_limit) }}
                          </small>
                          <small v-if="isMemberDeleted || isMemberSuspended" class="text-muted">
                            Coverage {{ isMemberDeleted ? 'terminated' : 'suspended' }}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <ul class="list-unstyled mb-4">
                      <li class="hstack justify-content-between mb-4">
                        <span class="text-muted fw-medium hstack gap-3">
                          <i class="bx bx-map fs-15"></i>Address
                        </span>
                        <a href="javascript:void(0);" class="float-end fw-bold text-black">
                          {{ memberInfo.address || 'N/A' }}, {{ memberInfo.woredaName }}
                        </a>
                      </li>
                      <li class="hstack justify-content-between mb-4">
                        <span class="text-muted fw-medium hstack gap-3">
                          <i class="bx bx-phone fs-15"></i>Phone
                        </span>
                        <a href="javascript:void(0);" class="float-end fw-bold text-black">
                          {{ memberInfo.phoneNumber || 'N/A' }}
                        </a>
                      </li>
                      <li class="hstack justify-content-between mb-0">
                        <span class="text-muted fw-medium hstack gap-3">
                          <i class="bx bx-envelope fs-15"></i>Email
                        </span>
                        <a href="javascript:void(0);" class="float-end fw-bold text-black">
                          {{ memberInfo.emailAddress || 'N/A' }}
                        </a>
                      </li>
                    </ul>
                  </div>

                  <!-- Card Actions Section -->
                  <div class="d-flex gap-2 text-center pt-4">
                    <!-- Suspend/Archive button -->
                    <button type="button"
                            @click="suspendMember"
                            :class="getSuspendButtonClass()"
                            :disabled="isMemberDeleted || isPermanentlyArchived">
                      <i :class="getSuspendButtonIcon()"></i>
                      <span>{{ getSuspendButtonText() }}</span>
                    </button>

                    <!-- Delete button -->
                    <button type="button"
                            @click="deleteMember"
                            :class="isMemberDeleted ? 'btn btn-secondary waves-effect disabled' : 'btn btn-outline-danger waves-effect'"
                            :disabled="isMemberDeleted || isMemberSuspended">
                      <i class="bx bx-trash me-2"></i>
                      <span>{{ isMemberDeleted ? 'Deleted' : 'Delete' }}</span>
                    </button>

                    <!-- ✅ NEW: View/Issue Card button -->
                    <button type="button"
                            class="btn btn-info waves-effect"
                            @click="viewCard"
                            :disabled="isMemberDeleted || isMemberSuspended"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            :title="getCardButtonTooltip()">
                      <i class="bx bx-id-card me-2"></i>
                      <span>{{ isCardIssued ? 'View Card' : 'Issue Card' }}</span>
                    </button>

                    <!-- Edit button -->
                    <button type="button"
                            :class="getEditButtonClass()"
                            @click="(isMemberDeleted || isMemberSuspended) ? null : editMember(memberId)"
                            :disabled="isMemberDeleted || isMemberSuspended"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            :title="getEditButtonTooltip()">
                      <i class="bx bxs-edit-alt me-1"></i>
                      <span>{{ getEditButtonText() }}</span>
                    </button>
                  </div>

                </div>
              </div>
            </div>

            <div class="col-xxl-8 col-xl-6">
              <div class="card min-h-90vh-xl">
                <div class="card-header bg-white p-0">
                  <!-- Nav tabs -->
                  <ul class="nav nav-tabs custom-nav-tabs" role="tablist">
                    <li class="nav-item" role="presentation">
                      <a class="nav-link active" data-bs-toggle="tab" href="#overview" role="tab" aria-selected="true">
                        <span class="d-block d-sm-none"><i class="fas fa-home"></i></span>
                        <span class="d-none d-sm-block">Overview</span>
                      </a>
                    </li>
                    <li class="nav-item" role="presentation" v-if="selectedPlan && memberInfo.enrollmentType === 'cbhi_member'">
                      <a class="nav-link" data-bs-toggle="tab" href="#coverage" role="tab" aria-selected="false">
                        <span class="d-block d-sm-none"><i class="bx bx-shield-check"></i></span>
                        <span class="d-none d-sm-block">Coverage Details</span>
                      </a>
                    </li>
                    <li class="nav-item" role="presentation">
                      <a class="nav-link" data-bs-toggle="tab" href="#beneficiaries" role="tab" aria-selected="false">
                        <span class="d-block d-sm-none"><i class="far fa-user"></i></span>
                        <span class="d-none d-sm-block">Beneficiaries</span>
                      </a>
                    </li>
                    <li class="nav-item" role="presentation">
                      <a class="nav-link" data-bs-toggle="tab" href="#claims" role="tab" aria-selected="false">
                        <span class="d-block d-sm-none"><i class="fas fa-exchange-alt"></i></span>
                        <span class="d-none d-sm-block">Claims</span>
                      </a>
                    </li>
                    <li class="nav-item" role="presentation">
                      <a class="nav-link" data-bs-toggle="tab" href="#payments" role="tab" aria-selected="false">
                        <span class="d-block d-sm-none"><i class="fas fa-cog"></i></span>
                        <span class="d-none d-sm-block">Payments</span>
                      </a>
                    </li>
                    <li class="nav-item" role="presentation" v-if="hasSuspensionHistory">
                      <a class="nav-link" data-bs-toggle="tab" href="#suspension-history" role="tab" aria-selected="false">
                        <span class="d-block d-sm-none"><i class="fas fa-history"></i></span>
                        <span class="d-none d-sm-block">Suspension History</span>
                      </a>
                    </li>
                  </ul>
                </div>
                <div class="card-body">
                  <div class="tab-content p-3 text-muted">
                    <!-- Overview Tab -->
                    <div class="tab-pane active show" id="overview" role="tabpanel">
                      <div class="row">
                        <div class="col-12 mb-4">
                          <div :class="getMemberStatusAlertClass()" role="alert">
                            <div class="me-4 d-none d-md-block">
                              <i :class="getMemberStatusIcon()"></i>
                            </div>
                            <div class="flex-grow-1">
                              <p class="fw-bold mb-1 text-truncate-1-line">Member Account Status</p>
                              <p class="fs-12 fw-medium mb-2">
                                Member ID: <strong>{{ memberInfo.cbhiNumber }}</strong> |
                                <span v-if="selectedPlan">
                                  Plan: <strong>{{ selectedPlan.plan_name }}</strong> |
                                </span>
                                Annual Premium: <strong>ETB {{ formatCurrency(annualPremium) }}</strong>
                                {{ paymentStatusFromAPI === 'PAID_IN_FULL' ? '(Paid)' : '(Pending)' }} |
                                Covering <strong>{{ totalBeneficiaries }} members</strong>
                              </p>
                              <p class="fs-12 text-muted mb-3">
                                <i class="bx bx-calendar-check me-1"></i>
                                Renewal Date: <strong>{{ formatDate(memberInfo.expiryDate) }}</strong> |
                                <i class="bx bx-shield-check ms-2 me-1"></i>
                                Status: <span class="badge" :class="getStatusBadgeClass()">
                                  {{ getStatusText() }}
                                </span>
                              </p>

                              <div class="d-flex flex-wrap gap-2">
                                <a href="javascript:void(0);" @click="switchTab('beneficiaries')"
                                   class="btn btn-sm btn-outline-secondary">
                                  <i class="bx bx-group me-1"></i>
                                  View Beneficiaries
                                </a>
                                <a href="javascript:void(0);" @click="switchTab('payments')"
                                   class="btn btn-sm btn-outline-info">
                                  <i class="bx bx-history me-1"></i>
                                  Payment History
                                </a>
                                <a v-if="selectedPlan && memberInfo.enrollmentType === 'cbhi_member'"
                                   href="javascript:void(0);" @click="switchTab('coverage')"
                                   class="btn btn-sm btn-outline-primary">
                                  <i class="bx bx-shield-check me-1"></i>
                                  View Coverage
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="col-12 mt-3">
                          <h4>Profile Details:</h4>
                          <div class="table-responsive">
                            <table class="table border table-sm">
                              <tbody>
                              <tr>
                                <td>Full Name</td>
                                <th>{{ memberFullName }}</th>
                              </tr>
                              <tr v-if="memberInfo.grandfatherName">
                                <td>Grandfather's Name</td>
                                <th>{{ memberInfo.grandfatherName }}</th>
                              </tr>
                              <tr>
                                <td>Gender</td>
                                <th>{{ capitalizeFirst(memberInfo.gender || '') }}</th>
                              </tr>
                              <tr>
                                <td>Date of Birth</td>
                                <th>{{ formatDate(memberInfo.dateOfBirth) }}</th>
                              </tr>
                              <tr>
                                <td>Age</td>
                                <th>{{ calculateAge(memberInfo.dateOfBirth) }} years</th>
                              </tr>
                              <tr>
                                <td>Marital Status</td>
                                <th>{{ capitalizeFirst(memberInfo.maritalStatus || '') }}</th>
                              </tr>
                              <tr v-if="memberInfo.nationalId">
                                <td>National ID</td>
                                <th>{{ memberInfo.nationalId }}</th>
                              </tr>
                              <tr>
                                <td>Region</td>
                                <th>{{ memberInfo.regionName }}</th>
                              </tr>
                              <tr>
                                <td>Zone</td>
                                <th>{{ memberInfo.zoneName }}</th>
                              </tr>
                              <tr>
                                <td>Woreda</td>
                                <th>{{ memberInfo.woredaName }}</th>
                              </tr>
                              <tr>
                                <td>Kebele</td>
                                <th>{{ memberInfo.kebeleName || 'N/A' }}</th>
                              </tr>
                              <tr v-if="memberInfo.occupation">
                                <td>Occupation</td>
                                <th>{{ memberInfo.occupation }}</th>
                              </tr>
                              <tr v-if="memberInfo.monthlyIncome">
                                <td>Monthly Income</td>
                                <th>{{ formatCurrency(memberInfo.monthlyIncome) }} ETB</th>
                              </tr>
                              <tr>
                                <td>Household Size</td>
                                <th>{{ memberInfo.householdSize }} members</th>
                              </tr>
                              <tr>
                                <td>CBHI Member ID</td>
                                <th>{{ memberInfo.cbhiNumber }}</th>
                              </tr>
                              <tr>
                                <td>Card Issued</td>
                                <th>
                                  <span v-if="memberInfo.cbhiCardIssued === '1' || memberInfo.cbhiCardIssued === 1 || memberInfo.cbhiCardIssued === true" class="badge bg-success">
                                    <i class="bx bx-id-card me-1"></i> Card Issued
                                  </span>
                                  <span v-else class="badge bg-warning text-dark">
                                    <i class="bx bx-id-card me-1"></i> Card Not Issued
                                  </span>
                                </th>
                              </tr>
                              <tr v-if="memberInfo.cbhiCardIssued === '1' || memberInfo.cbhiCardIssued === 1 || memberInfo.cbhiCardIssued === true">
                                <td>Card Issue Date</td>
                                <th>{{ formatDate(memberInfo.cardIssueDate) }}</th>
                              </tr>
                              <tr v-if="memberInfo.cbhiCardIssued === '1' || memberInfo.cbhiCardIssued === 1 || memberInfo.cbhiCardIssued === true">
                                <td>Card Issue Notes</td>
                                <th class="text-primary fw-bold">{{ memberInfo.cardIssueNotes || 'N/A' }}</th>
                              </tr>
                              <tr>
                                <td>Membership Type</td>
                                <th>{{ capitalizeFirst(memberInfo.membershipType || '') }}</th>
                              </tr>
                              <tr>
                                <td>Registration Date</td>
                                <th>{{ formatDate(memberInfo.registrationDate) }}</th>
                              </tr>
                              <tr v-if="memberInfo.registeredBy">
                                <td>Registered By</td>
                                <th>{{ memberInfo.registeredBy }}</th>
                              </tr>
                              <tr v-if="memberInfo.suspensionDate">
                                <td class="text-warning">Suspension Date</td>
                                <th class="text-warning">{{ formatDate(memberInfo.suspensionDate) }}</th>
                              </tr>
                              <tr v-if="memberInfo.suspensionCategory">
                                <td class="text-warning">Suspension Category</td>
                                <th class="text-warning">{{ formatSuspensionCategory(memberInfo.suspensionCategory) }}</th>
                              </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- NEW: Coverage Details Tab -->
                    <div class="tab-pane" id="coverage" role="tabpanel" v-if="selectedPlan && memberInfo.enrollmentType === 'cbhi_member'">
                      <div class="row">
                        <!-- Plan Summary Card -->
                        <div class="col-12 mb-4">
                          <div class="card border-primary">
                            <div class="card-header text-white d-flex justify-content-between align-items-center">
                              <h6 class="mb-0 text-black">
                                <i class="bx bx-shield-check me-2"></i>
                                Current Health Insurance Plan
                              </h6>
                              <button class="btn btn-outline-light btn-sm" @click="showPlanChangeModal" :disabled="isMemberDeleted || isMemberSuspended">
                                <i class="bx bx-sync me-1"></i>
                                Change Plan
                              </button>
                            </div>
                            <div class="card-body">
                              <div class="row">
                                <div class="col-md-8">
                                  <h5 class="mb-2">
                                    {{ selectedPlan.plan_name }}
                                    <span class="badge ms-2" :class="getPlanTypeBadgeClass(selectedPlan.plan_type)">
                                      {{ selectedPlan.plan_type.toUpperCase() }}
                                    </span>
                                  </h5>
                                  <p class="text-muted mb-3">{{ selectedPlan.plan_description }}</p>
                                  <div class="row">
                                    <div class="col-sm-6 mb-2">
                                      <strong class="text-primary">Annual Coverage:</strong><br>
                                      <span class="h5 text-success">ETB {{ formatCurrency(selectedPlan.annual_coverage_limit) }}</span>
                                    </div>
                                    <div class="col-sm-6 mb-2">
                                      <strong class="text-primary">Per Visit Limit:</strong><br>
                                      <span class="h6">ETB {{ formatCurrency(selectedPlan.per_visit_limit) }}</span>
                                    </div>
                                  </div>
                                </div>
                                <div class="col-md-4">
                                  <div class="text-center premium-summary p-3 bg-light rounded">
                                    <h6 class="mb-2">Your Premium</h6>
                                    <div class="h4 text-primary mb-2">ETB {{ formatCurrency(annualPremium) }}</div>
                                    <small class="text-muted">
                                      {{ totalBeneficiaries }} total members covered
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Covered Services -->
                        <div class="col-12 mb-4" v-if="selectedPlan.benefits && selectedPlan.benefits.services">
                          <div class="card">
                            <div class="card-header">
                              <h6 class="mb-0">
                                <i class="bx bx-list-check me-2"></i>
                                Covered Services ({{ selectedPlan.benefits.services.length }} total)
                              </h6>
                            </div>
                            <div class="card-body">
                              <div v-for="(services, category) in groupServicesByCategory(selectedPlan.benefits.services)" :key="category" class="service-category mb-4">
                                <h6 class="category-title text-primary mb-3">
                                  <i class="bx bx-check-shield me-2"></i>
                                  {{ category }}
                                </h6>
                                <div class="row">
                                  <div v-for="service in services" :key="service.id" class="col-md-6 col-lg-4 mb-3">
                                    <div class="service-card p-3 border rounded bg-light">
                                      <div class="d-flex align-items-center mb-2">
                                        <i class="bx bx-check-circle text-success me-2"></i>
                                        <strong>{{ service.name }}</strong>
                                      </div>
                                      <small class="text-muted">Code: {{ service.code }}</small>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Coverage Rules & Thresholds -->
                        <div class="col-12" v-if="selectedPlan.coverage_configs">
                          <div class="card">
                            <div class="card-header">
                              <h6 class="mb-0">
                                <i class="bx bx-cog me-2"></i>
                                Coverage Rules & Settings
                              </h6>
                            </div>
                            <div class="card-body">
                              <div class="row">
                                <!-- Coverage Percentages -->
                                <div class="col-lg-6 mb-4">
                                  <h6 class="text-primary mb-3">Coverage Percentages</h6>
                                  <div class="coverage-rules">
                                    <div class="rule-item mb-2" v-if="selectedPlan.coverage_configs.default_coverage_percentage">
                                      <div class="d-flex justify-content-between">
                                        <span>Standard Coverage:</span>
                                        <strong class="text-success">{{ selectedPlan.coverage_configs.default_coverage_percentage.value }}%</strong>
                                      </div>
                                    </div>
                                    <div class="rule-item mb-2" v-if="selectedPlan.coverage_configs.emergency_coverage_percentage">
                                      <div class="d-flex justify-content-between">
                                        <span>Emergency Services:</span>
                                        <strong class="text-success">{{ selectedPlan.coverage_configs.emergency_coverage_percentage.value }}%</strong>
                                      </div>
                                    </div>
                                    <div class="rule-item mb-2" v-if="selectedPlan.coverage_configs.preventive_care_coverage_percentage">
                                      <div class="d-flex justify-content-between">
                                        <span>Preventive Care:</span>
                                        <strong class="text-success">{{ selectedPlan.coverage_configs.preventive_care_coverage_percentage.value }}%</strong>
                                      </div>
                                    </div>
                                    <div class="rule-item mb-2" v-if="selectedPlan.coverage_configs.maternity_coverage_percentage">
                                      <div class="d-flex justify-content-between">
                                        <span>Maternity Services:</span>
                                        <strong class="text-success">{{ selectedPlan.coverage_configs.maternity_coverage_percentage.value }}%</strong>
                                      </div>
                                    </div>
                                    <div class="rule-item mb-2" v-if="selectedPlan.coverage_configs.dental_coverage_percentage">
                                      <div class="d-flex justify-content-between">
                                        <span>Dental Services:</span>
                                        <strong class="text-info">{{ selectedPlan.coverage_configs.dental_coverage_percentage.value }}%</strong>
                                      </div>
                                    </div>
                                    <div class="rule-item mb-2" v-if="selectedPlan.coverage_configs.vision_coverage_percentage">
                                      <div class="d-flex justify-content-between">
                                        <span>Vision Services:</span>
                                        <strong class="text-info">{{ selectedPlan.coverage_configs.vision_coverage_percentage.value }}%</strong>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <!-- Thresholds & Limits -->
                                <div class="col-lg-6 mb-4">
                                  <h6 class="text-primary mb-3">Thresholds & Requirements</h6>
                                  <div class="threshold-rules">
                                    <div class="rule-item mb-2" v-if="selectedPlan.coverage_configs.referral_required_above_threshold">
                                      <div class="d-flex justify-content-between">
                                        <span>Referral Required Above:</span>
                                        <strong class="text-warning">ETB {{ formatCurrency(selectedPlan.coverage_configs.referral_required_above_threshold.value) }}</strong>
                                      </div>
                                    </div>
                                    <div class="rule-item mb-2" v-if="selectedPlan.coverage_configs.preauth_required_above_threshold">
                                      <div class="d-flex justify-content-between">
                                        <span>Pre-auth Required Above:</span>
                                        <strong class="text-warning">ETB {{ formatCurrency(selectedPlan.coverage_configs.preauth_required_above_threshold.value) }}</strong>
                                      </div>
                                    </div>
                                    <div class="rule-item mb-2" v-if="selectedPlan.coverage_configs.above_limit_topup_enabled && selectedPlan.coverage_configs.above_limit_topup_enabled.value">
                                      <div class="d-flex justify-content-between">
                                        <span>Top-up Coverage:</span>
                                        <strong class="text-info">{{ selectedPlan.coverage_configs.above_limit_topup_percentage?.value || 0 }}% above limit</strong>
                                      </div>
                                    </div>
                                    <div class="rule-item mb-2" v-if="selectedPlan.coverage_configs.specialist_referral_required">
                                      <div class="d-flex justify-content-between">
                                        <span>Specialist Referral:</span>
                                        <strong class="text-secondary">{{ selectedPlan.coverage_configs.specialist_referral_required.value ? 'Required' : 'Not Required' }}</strong>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <!-- Waiting Periods -->
                                <div class="col-12">
                                  <h6 class="text-primary mb-3">Waiting Periods</h6>
                                  <div class="row">
                                    <div class="col-sm-6 col-md-3 mb-2" v-if="selectedPlan.coverage_configs.waiting_period_new_members">
                                      <div class="waiting-period-card p-2 bg-light rounded text-center">
                                        <strong class="d-block">{{ selectedPlan.coverage_configs.waiting_period_new_members.value }}</strong>
                                        <small class="text-muted">Days for New Members</small>
                                      </div>
                                    </div>
                                    <div class="col-sm-6 col-md-3 mb-2" v-if="selectedPlan.coverage_configs.waiting_period_maternity">
                                      <div class="waiting-period-card p-2 bg-light rounded text-center">
                                        <strong class="d-block">{{ selectedPlan.coverage_configs.waiting_period_maternity.value }}</strong>
                                        <small class="text-muted">Days for Maternity</small>
                                      </div>
                                    </div>
                                    <div class="col-sm-6 col-md-3 mb-2" v-if="selectedPlan.coverage_configs.waiting_period_dental">
                                      <div class="waiting-period-card p-2 bg-light rounded text-center">
                                        <strong class="d-block">{{ selectedPlan.coverage_configs.waiting_period_dental.value }}</strong>
                                        <small class="text-muted">Days for Dental</small>
                                      </div>
                                    </div>
                                    <div class="col-sm-6 col-md-3 mb-2" v-if="selectedPlan.coverage_configs.waiting_period_preexisting">
                                      <div class="waiting-period-card p-2 bg-light rounded text-center">
                                        <strong class="d-block">{{ selectedPlan.coverage_configs.waiting_period_preexisting.value }}</strong>
                                        <small class="text-muted">Days for Pre-existing</small>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- ✅ UPDATED: Beneficiaries Tab with Disability/Dependency Information -->
                    <div class="tab-pane" id="beneficiaries" role="tabpanel">
                      <div class="col-12 mb-3">
                        <div class="d-flex justify-content-between align-items-center">
                          <h4 class="mb-0 pb-0">
                            {{ getBeneficiariesTitle() }}
                          </h4>
                          <button v-if="!isMemberDeleted && !isMemberSuspended"
                                  class="btn btn-success btn-sm"
                                  @click="addBeneficiary">
                            <i class="bx bx-plus-circle me-1"></i> Add Beneficiary
                          </button>
                          <span v-else class="badge" :class="isMemberDeleted ? 'bg-danger' : 'bg-warning'">
                            <i class="bx bx-lock me-1"></i> {{ isMemberDeleted ? 'Record Archived' : 'Account Suspended' }}
                          </span>
                        </div>
                      </div>

                      <!-- Plan Coverage Rules Summary -->
                      <div v-if="selectedPlan && !isMemberDeleted && !isMemberSuspended" class="col-12 mb-3">
                        <div class="alert alert-info py-2 mb-3">
                          <div class="d-flex flex-wrap align-items-center gap-3">
                            <small>
                              <i class="bx bx-info-circle me-1"></i>
                              <strong>Plan Rules:</strong>
                            </small>
                            <small>
                              <span class="badge bg-success bg-opacity-10 text-success">
                                Free Spouses: {{ getPlanFreeSpousesText() }}
                              </span>
                            </small>
                            <small>
                              <span class="badge bg-success bg-opacity-10 text-success">
                                Free Children Under: {{ getPlanAdultChildThreshold() }} years
                              </span>
                            </small>
                            <small>
                              <span class="badge bg-info bg-opacity-10 text-info">
                                Additional Rate: {{ getPlanAdditionalMemberRate() }}
                              </span>
                            </small>
                            <small>
                              <span class="badge bg-primary bg-opacity-10 text-primary">
                                <i class="bx bx-heart me-1"></i>
                                Disabled/Dependent Adult Children: FREE
                              </span>
                            </small>
                          </div>
                        </div>
                      </div>

                      <div class="col-12">
                        <div class="table-responsive">
                          <table class="table align-middle table-hover contacts-table border table-striped">
                            <thead class="table-light">
                            <tr>
                              <th scope="col" style="width: 50px;">No.</th>
                              <th scope="col">Name & Details</th>
                              <th scope="col">Relationship</th>
                              <th scope="col">Gender</th>
                              <th scope="col">Age</th>
                              <th scope="col">Special Status</th>
                              <th scope="col">Status</th>
                              <th scope="col">Cost</th>
                              <th class="text-center" style="width: 120px;">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <!-- Primary Member Row -->
                            <tr>
                              <td>1.</td>
                              <td>
                                <div class="d-flex align-items-center">
                                  <div class="position-relative me-3">
                                    <!-- Primary Member Photo -->
                                    <img v-if="hasProfilePhoto"
                                         :src="getPhotoUrl()"
                                         class="rounded-circle border border-2 border-primary photo-clickable"
                                         style="width: 45px; height: 45px; object-fit: cover;"
                                         :alt="memberFullName"
                                         @click="viewPhotoFullScreen"
                                         @error="onImageError"
                                         data-bs-toggle="tooltip"
                                         title="Click to view photo">

                                    <div v-else
                                         class="bg-primary rounded-circle d-flex align-items-center justify-content-center photo-clickable"
                                         style="width: 45px; height: 45px;"
                                         @click="openPhotoModal"
                                         data-bs-toggle="tooltip"
                                         title="Click to add photo">
                                      <span class="text-white fw-bold">{{ getInitials(memberInfo) }}</span>
                                    </div>

                                    <!-- Primary Member Photo Update Button -->
                                    <button v-if="!isMemberDeleted && !isMemberSuspended"
                                            @click.stop="openPhotoModal"
                                            class="btn btn-sm btn-primary rounded-circle position-absolute member-photo-btn"
                                            style="bottom: -2px; right: -2px; width: 22px; height: 22px; padding: 0;"
                                            title="Update Photo">
                                      <i class="bx bx-camera" style="font-size: 11px;"></i>
                                    </button>
                                  </div>

                                  <div>
                                    <strong>{{ memberFullName }}</strong>
                                    <span class="badge bg-primary ms-2">Primary</span>
                                    <p class="mb-0 text-muted small">
                                      {{ calculateAge(memberInfo.dateOfBirth) }} Year Old {{ capitalizeFirst(memberInfo.gender || '') }}
                                      <span v-if="memberInfo.phoneNumber" class="ms-2">
                                        <i class="bx bx-phone"></i> {{ memberInfo.phoneNumber }}
                                      </span>
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span class="badge bg-primary">Self</span>
                              </td>
                              <td>{{ capitalizeFirst(memberInfo.gender || '') }}</td>
                              <td>{{ calculateAge(memberInfo.dateOfBirth) }}</td>
                              <td>
                                <span class="badge bg-light text-muted">N/A</span>
                              </td>
                              <td>
                                <span class="badge" :class="getStatusBadgeClass()">
                                  {{ capitalizeFirst(memberInfo.status || 'unknown') }}
                                </span>
                              </td>
                              <td>
                                <strong v-if="selectedPlan && !isMemberSuspended">
                                  ETB {{ formatCurrency(selectedPlan.primary_annual_amount) }}
                                </strong>
                                <span v-else class="text-muted">N/A</span>
                              </td>
                              <td class="text-center">
                                <button class="btn btn-outline-primary btn-sm"
                                        @click="editMember(memberId)"
                                        :disabled="isMemberDeleted || isMemberSuspended"
                                        data-bs-toggle="tooltip"
                                        title="Edit primary member">
                                  <i class="bx bx-edit"></i>
                                </button>
                              </td>
                            </tr>

                            <!-- ✅ UPDATED: Beneficiaries Rows with Disability/Dependency Display -->
                            <tr v-for="(beneficiary, index) in beneficiaries" :key="beneficiary.beneficiary_id">
                              <td>{{ index + 2 }}.</td>
                              <td>
                                <div class="d-flex align-items-center">
                                  <!-- Beneficiary Photo/Avatar with Update Button -->
                                  <div class="position-relative me-3">
                                    <img v-if="beneficiaryHasPhoto(beneficiary)"
                                         :src="getBeneficiaryPhotoUrl(beneficiary)"
                                         class="rounded-circle border border-2 border-secondary photo-clickable"
                                         style="width: 45px; height: 45px; object-fit: cover;"
                                         :alt="beneficiary.full_name || beneficiary.fullName || 'Beneficiary'"
                                         @click="viewBeneficiaryPhoto(beneficiary)"
                                         @error="onBeneficiaryImageError($event, beneficiary)"
                                         data-bs-toggle="tooltip"
                                         title="Click to view photo">

                                    <div v-else
                                         class="bg-primary rounded-circle d-flex align-items-center justify-content-center photo-clickable"
                                         style="width: 45px; height: 45px;"
                                         @click="openBeneficiaryPhotoModal(beneficiary)"
                                         data-bs-toggle="tooltip"
                                         title="Click to add photo">
                                      <span class="avatar-title text-white">{{ getBeneficiaryInitials(beneficiary) }}</span>
                                    </div>

                                    <!-- Photo Update Button for Active Beneficiaries -->
                                    <button v-if="!isMemberDeleted && !isMemberSuspended && beneficiary.status === 'active'"
                                            @click.stop="openBeneficiaryPhotoModal(beneficiary)"
                                            class="btn btn-sm btn-primary rounded-circle position-absolute beneficiary-photo-btn"
                                            style="bottom: -2px; right: -2px; width: 22px; height: 22px; padding: 0;"
                                            title="Update Photo">
                                      <i class="bx bx-camera" style="font-size: 11px;"></i>
                                    </button>

                                    <!-- Status Indicator for Inactive Beneficiaries -->
                                    <span v-if="beneficiary.status !== 'active'"
                                          class="position-absolute top-0 start-100 translate-middle badge rounded-pill"
                                          :class="beneficiary.status === 'deleted' ? 'bg-danger' : 'bg-warning'"
                                          style="font-size: 8px; padding: 2px 4px;">
                                      <i :class="beneficiary.status === 'deleted' ? 'bx bx-x' : 'bx bx-pause'"></i>
                                    </span>
                                  </div>

                                  <div>
                                    <strong :class="{ 'text-muted': isMemberDeleted || isMemberSuspended || beneficiary.status === 'deleted' }">
                                      {{ beneficiary.full_name || beneficiary.fullName || 'Unknown' }}
                                    </strong>
                                    <span v-if="beneficiary.status === 'deleted'" class="badge bg-danger ms-1">Deleted</span>
                                    <span v-else-if="beneficiary.status === 'suspended'" class="badge bg-warning ms-1">Suspended</span>

                                    <p class="mb-0 text-muted small">
                                      {{ calculateAge(beneficiary.date_of_birth || beneficiary.dateOfBirth) }} Year Old {{ capitalizeFirst(beneficiary.gender || '') }}
                                      <span v-if="beneficiary.phone_number || beneficiary.phoneNumber" class="ms-2">
                                        <i class="bx bx-phone"></i> {{ beneficiary.phone_number || beneficiary.phoneNumber }}
                                      </span>
                                      <span v-if="beneficiary.id_number || beneficiary.idNumber" class="ms-2">
                                        <i class="bx bx-id-card"></i> {{ beneficiary.id_number || beneficiary.idNumber }}
                                      </span>
                                    </p>

                                    <!-- ✅ NEW: Health Info Display -->
                                    <div v-if="beneficiary.blood_group || beneficiary.bloodGroup || beneficiary.chronic_conditions || beneficiary.chronicConditions" class="mt-1">
                                      <span v-if="beneficiary.blood_group || beneficiary.bloodGroup" class="badge bg-info bg-opacity-10 text-info me-1">
                                        <i class="bx bx-droplet"></i> {{ beneficiary.blood_group || beneficiary.bloodGroup }}
                                      </span>
                                      <span v-if="beneficiary.chronic_conditions || beneficiary.chronicConditions" class="badge bg-warning bg-opacity-10 text-warning">
                                        <i class="bx bx-first-aid"></i> Has Conditions
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span class="badge bg-info">{{ capitalizeFirst(beneficiary.relationship || '') }}</span>
                              </td>
                              <td>
                                <i :class="beneficiary.gender === 'male' ? 'bx bx-male text-primary' : 'bx bx-female text-danger'"></i>
                                {{ capitalizeFirst(beneficiary.gender || '') }}
                              </td>
                              <td>
                                <span :class="calculateAge(beneficiary.date_of_birth || beneficiary.dateOfBirth) < 5 ? 'text-warning fw-bold' : ''">
                                  {{ calculateAge(beneficiary.date_of_birth || beneficiary.dateOfBirth) }}
                                </span>
                                <span v-if="calculateAge(beneficiary.date_of_birth || beneficiary.dateOfBirth) < 5" class="text-muted small d-block">
                                  (Child)
                                </span>
                              </td>

                              <!-- ✅ NEW: Special Status Column with Disability/Dependency Display -->
                              <td>
                                <div class="d-flex flex-column gap-1">
                                  <!-- Disability Badge -->
                                  <span v-if="beneficiary.hasDisability || beneficiary.has_disability"
                                        class="badge bg-danger bg-opacity-15 text-danger"
                                        data-bs-toggle="tooltip"
                                        :title="beneficiary.disabilityNotes || beneficiary.disability_notes || 'Has disability'">
                                    <i class="bx bx-accessibility me-1"></i>
                                    Disability
                                  </span>

                                  <!-- Dependency Badge -->
                                  <span v-if="beneficiary.isDependent || beneficiary.is_dependent"
                                        class="badge bg-purple bg-opacity-15 text-purple"
                                        data-bs-toggle="tooltip"
                                        :title="beneficiary.dependencyNotes || beneficiary.dependency_notes || 'Dependent (no income)'">
                                    <i class="bx bx-home-heart me-1"></i>
                                    Dependent
                                  </span>

                                  <!-- No Special Status -->
                                  <span v-if="!beneficiary.hasDisability && !beneficiary.has_disability && !beneficiary.isDependent && !beneficiary.is_dependent"
                                        class="badge bg-light text-muted">
                                    <i class="bx bx-minus"></i>
                                    None
                                  </span>
                                </div>
                              </td>

                              <td>
                                <span class="badge" :class="getBeneficiaryStatusBadgeClass(beneficiary)">
                                  {{ getBeneficiaryStatusText(beneficiary) }}
                                </span>
                              </td>

                              <!-- ✅ UPDATED: Cost Column with Special Case Logic -->
                              <td>
                                <span v-if="isMemberDeleted || isMemberSuspended || beneficiary.status !== 'active'" class="text-muted">
                                  N/A
                                </span>
                                <!-- Free for spouse (within limit) -->
                                <span v-else-if="isBeneficiaryFreeSpouse(beneficiary, index)" class="badge bg-success">
                                  <i class="bx bx-check me-1"></i>Free (Spouse)
                                </span>
                                <!-- Free for children under threshold -->
                                <span v-else-if="isBeneficiaryFreeChild(beneficiary)" class="badge bg-success">
                                  <i class="bx bx-check me-1"></i>Free (Child)
                                </span>
                                <!-- ✅ NEW: Free for adult children with disability -->
                                <span v-else-if="isBeneficiaryFreeDisability(beneficiary)" class="badge bg-info">
                                  <i class="bx bx-accessibility me-1"></i>Free (Disability)
                                </span>
                                <!-- ✅ NEW: Free for adult children who are dependents -->
                                <span v-else-if="isBeneficiaryFreeDependency(beneficiary)" class="badge bg-purple text-white">
                                  <i class="bx bx-home-heart me-1"></i>Free (Dependent)
                                </span>
                                <!-- Paid beneficiary -->
                                <strong v-else-if="selectedPlan">
                                  ETB {{ formatCurrency(getAdditionalMemberFee()) }}
                                </strong>
                                <span v-else class="text-muted">-</span>
                              </td>

                              <td class="text-center">
                                <div v-if="!isMemberDeleted && !isMemberSuspended && beneficiary.status === 'active'"
                                     class="btn-group"
                                     role="group">
                                  <button class="btn btn-outline-info btn-sm"
                                          @click="viewBeneficiaryDetails(beneficiary)"
                                          data-bs-toggle="tooltip"
                                          title="View details">
                                    <i class="bx bx-show"></i>
                                  </button>
                                  <button class="btn btn-outline-primary btn-sm"
                                          @click="editBeneficiary(beneficiary)"
                                          data-bs-toggle="tooltip"
                                          title="Edit beneficiary">
                                    <i class="bx bx-edit"></i>
                                  </button>
                                  <button class="btn btn-outline-danger btn-sm"
                                          @click="removeBeneficiary(beneficiary)"
                                          data-bs-toggle="tooltip"
                                          title="Remove beneficiary">
                                    <i class="bx bx-trash"></i>
                                  </button>
                                </div>
                                <button v-else class="btn btn-secondary btn-sm" disabled>
                                  <i class="bx bx-lock me-1"></i>
                                  {{ getInactiveBeneficiaryButtonText(beneficiary) }}
                                </button>
                              </td>
                            </tr>
                            </tbody>

                            <!-- ✅ UPDATED: Table Footer with Enhanced Summary -->
                            <tfoot v-if="beneficiaries.length > 0" class="table-light">
                            <tr>
                              <td colspan="5" class="text-end fw-bold">Total Members:</td>
                              <td colspan="4">
                                <strong>{{ totalBeneficiaries }}</strong>
                                <span class="text-muted ms-2">
                                  (1 Primary + {{ beneficiaries.length }} Beneficiaries)
                                </span>
                              </td>
                            </tr>

                            <!-- ✅ NEW: Beneficiary Breakdown Row -->
                            <tr v-if="selectedPlan">
                              <td colspan="5" class="text-end">Beneficiary Breakdown:</td>
                              <td colspan="4">
                                <div class="d-flex flex-wrap gap-2">
                                  <span class="badge bg-success bg-opacity-10 text-success">
                                    <i class="bx bx-check me-1"></i>Free: {{ countFreeBeneficiaries() }}
                                  </span>
                                  <span v-if="countDisabilityBeneficiaries() > 0" class="badge bg-info bg-opacity-10 text-info">
                                    <i class="bx bx-accessibility me-1"></i>Disability: {{ countDisabilityBeneficiaries() }}
                                  </span>
                                  <span v-if="countDependentBeneficiaries() > 0" class="badge bg-purple bg-opacity-10 text-purple">
                                    <i class="bx bx-home-heart me-1"></i>Dependent: {{ countDependentBeneficiaries() }}
                                  </span>
                                  <span v-if="countPaidBeneficiaries() > 0" class="badge bg-warning bg-opacity-10 text-warning">
                                    <i class="bx bx-money me-1"></i>Paid: {{ countPaidBeneficiaries() }}
                                  </span>
                                </div>
                              </td>
                            </tr>

                            <tr v-if="selectedPlan">
                              <td colspan="5" class="text-end fw-bold">Annual Premium:</td>
                              <td colspan="4">
                                <strong class="text-primary">ETB {{ formatCurrency(annualPremium) }}</strong>
                                <div class="text-muted small">
                                  From database (includes all dependents with special cases)
                                </div>
                              </td>
                            </tr>
                            </tfoot>
                          </table>
                        </div>

                        <!-- No beneficiaries message -->
                        <div v-if="beneficiaries.length === 0" class="text-center py-5">
                          <i class="bx bx-group display-1 text-muted"></i>
                          <p class="text-muted mb-3">
                            {{ getNoBeneficiariesMessage() }}
                          </p>
                          <button v-if="!isMemberDeleted && !isMemberSuspended"
                                  class="btn btn-primary"
                                  @click="addBeneficiary">
                            <i class="bx bx-plus-circle me-2"></i>Add First Beneficiary
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Claims Tab -->
                    <div class="tab-pane" id="claims" role="tabpanel">
                      <div class="col-12">
                        <h4 class="mb-3">Medical Claims History</h4>

                        <!-- Warning for suspended/deleted members -->
                        <div v-if="isMemberSuspended || isMemberDeleted" class="alert" :class="isMemberDeleted ? 'alert-danger' : 'alert-warning'" role="alert">
                          <i class="bx bx-info-circle me-2"></i>
                          {{ isMemberDeleted ? 'No new claims can be submitted for deleted members.' : 'Claims processing is suspended for this member.' }}
                        </div>

                        <!-- Claims Table -->
                        <div class="table-responsive">
                          <table class="table align-middle table-hover contacts-table border table-striped text-nowrap">
                            <thead class="table-light">
                            <tr>
                              <th scope="col">No.</th>
                              <th scope="col">Date</th>
                              <th scope="col">Beneficiary</th>
                              <th scope="col">Health Facility</th>
                              <th scope="col">Service Type</th>
                              <th scope="col">Amount</th>
                              <th scope="col">Status</th>
                              <th class="text-right">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(claim, index) in claims" :key="claim.claim_id">
                              <td>{{ index + 1 }}.</td>
                              <td>
                                <strong>{{ formatDate(claim.service_date) }}</strong>
                                <p class="mb-0 text-muted fs-12">Claim #{{ claim.claim_number }}</p>
                              </td>
                              <td>
                                <div class="d-flex align-items-center">
                                  <div class="avatar-sm bg-primary rounded-circle me-2">
                                    <span class="avatar-title text-white">{{ getClaimInitials(claim) }}</span>
                                  </div>
                                  <div>
                                    <strong>{{ getClaimantName(claim) }}</strong>
                                    <p class="mb-0 text-muted fs-12">{{ getClaimantRelationship(claim) }}</p>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <strong>{{ claim.hospital_name }}</strong>
                              </td>
                              <td>
                                <span class="badge bg-info">{{ claim.type_name }}</span>
                              </td>
                              <td>
                                <strong>{{ formatCurrency(claim.total_amount) }} ETB</strong>
                                <p v-if="claim.approved_amount" class="mb-0 text-muted fs-12">
                                  Approved: {{ formatCurrency(claim.approved_amount) }} ETB
                                </p>
                              </td>
                              <td>
                                <span class="badge" :class="getClaimStatusBadgeClass(claim)">
                                  <i :class="getClaimStatusIcon(claim)"></i>
                                  {{ capitalizeFirst(claim.status || '') }}
                                </span>
                              </td>
                              <td class="text-right">
                                <button class="btn btn-outline-primary btn-sm me-1" title="View Details"
                                        @click="viewClaimDetails(claim)">
                                  <i class="mdi mdi-file-document-outline"></i>
                                </button>
                                <button class="btn btn-outline-secondary btn-sm" title="Download Receipt"
                                        v-if="claim.status === 'approved' || claim.status === 'paid'"
                                        @click="downloadClaimReceipt(claim)">
                                  <i class="mdi mdi-download"></i>
                                </button>
                              </td>
                            </tr>
                            </tbody>
                          </table>

                          <!-- No claims message -->
                          <div v-if="claims.length === 0" class="text-center py-5">
                            <i class="mdi mdi-file-document-outline display-1 text-muted"></i>
                            <p class="text-muted">No claims submitted yet</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Payments Tab -->
                    <div class="tab-pane" id="payments" role="tabpanel">
                      <div class="col-12">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                          <h4>Premium Payments & Billing History</h4>
                          <button v-if="!isMemberDeleted && !isMemberSuspended" class="btn btn-success btn-sm" @click="recordPayment">
                            <i class="bx bx-plus-circle me-1"></i> Record Payment
                          </button>
                          <span v-else class="badge bg-secondary">
                            <i class="bx bx-lock me-1"></i> No New Payments
                          </span>
                        </div>

                        <div class="table-responsive">
                          <table class="table align-middle table-hover contacts-table border table-striped text-nowrap">
                            <thead class="table-light">
                            <tr>
                              <th scope="col">No.</th>
                              <th scope="col">Payment Date</th>
                              <th scope="col">Premium Amount</th>
                              <th scope="col">Method</th>
                              <th scope="col">Reference No.</th>
                              <th scope="col">Status</th>
                              <th scope="col">Collected By</th>
                              <th class="text-right">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(payment, index) in payments" :key="payment.payment_id">
                              <td>{{ index + 1 }}.</td>
                              <td>
                                <strong>{{ formatDate(payment.payment_date) }}</strong>
                                <p class="mb-0 text-muted fs-12">Receipt #{{ payment.receipt_number || payment.payment_reference }}</p>
                              </td>
                              <td>
                                <strong>{{ formatCurrency(payment.amount) }} ETB</strong>
                                <p class="mb-0 text-muted fs-12">Year {{ payment.payment_year }}</p>
                              </td>
                              <td>
                                <div class="d-flex align-items-center">
                                  <i :class="getPaymentMethodIcon(payment.method_name)" class="me-2 fs-5"></i>
                                  <div>
                                    <strong>{{ payment.method_name }}</strong>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <strong>{{ payment.bank_reference || payment.payment_reference }}</strong>
                              </td>
                              <td>
                                <span class="badge text-uppercase" :class="getPaymentStatusBadgeClass(payment)">
                                  <i class="bx bx-check me-1" v-if="payment.status === 'completed'"></i>
                                  {{ payment.status }}
                                </span>
                              </td>
                              <td>{{ payment.collected_by || 'System' }}</td>
                              <td class="text-right">
                                <button class="btn btn-outline-primary btn-sm me-1" title="View Receipt"
                                        @click="viewReceipt(payment)">
                                  <i class="bx bx-file-blank"></i>
                                </button>
                              </td>
                            </tr>
                            </tbody>
                          </table>

                          <!-- No payments message -->
                          <div v-if="payments.length === 0" class="text-center py-5">
                            <i class="bx bx-money display-1 text-muted"></i>
                            <p class="text-muted">
                              {{ getNoPaymentsMessage() }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Suspension History Tab -->
                    <div class="tab-pane" id="suspension-history" role="tabpanel" v-if="hasSuspensionHistory">
                      <div class="col-12">
                        <h4 class="mb-3">Suspension & Reactivation History</h4>
                        <div class="table-responsive">
                          <table class="table align-middle table-hover border table-striped">
                            <thead class="table-light">
                            <tr>
                              <th>Date</th>
                              <th>Action</th>
                              <th>Type</th>
                              <th>Category</th>
                              <th>Reason</th>
                              <th>By</th>
                              <th>Status</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="history in suspensionHistory" :key="history.suspension_id">
                              <td>{{ formatDate(history.suspension_date) }}</td>
                              <td>
                                <span class="badge bg-warning">Suspended</span>
                                <span v-if="history.reactivation_date" class="badge bg-success ms-1">
                                  Reactivated {{ formatDate(history.reactivation_date) }}
                                </span>
                              </td>
                              <td>{{ capitalizeFirst(history.suspension_type) }}</td>
                              <td>{{ formatSuspensionCategory(history.suspension_category) }}</td>
                              <td>{{ history.suspension_reason }}</td>
                              <td>{{ history.suspended_by_username }}</td>
                              <td>
                                <span class="badge" :class="history.status === 'active' ? 'bg-warning' : 'bg-success'">
                                  {{ capitalizeFirst(history.status) }}
                                </span>
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- container-fluid -->
      </div>
      <!-- End Page-content -->

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">© CBHI</div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">
                Ministry of Health - Ethiopia
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    <!-- end main content-->
  </div>
  <!-- END layout-wrapper -->

  <!-- Add Beneficiary Modal Component -->
  <BeneficiaryModal
      ref="beneficiaryModal"
      :cbhi-number="cbhiNumber"
      :current-beneficiary-count="beneficiaries.length"
      :member-info="memberInfo"
      :existing-beneficiaries="beneficiaries"
      :selected-plan="selectedPlan"
      @beneficiary-saved="handleBeneficiarySaved"
      @beneficiary-removed="handleBeneficiaryRemoved"
  />

  <!-- Payment Modal Component -->
  <PaymentModal
      ref="paymentModal"
      :member-info="memberInfo"
      :annual-premium="annualPremium"
      :total-beneficiaries="totalBeneficiaries"
      @payment-recorded="handlePaymentRecorded"
      @payment-error="handlePaymentError"
      @download-receipt="handleViewReceipt"
  />

  <!-- Receipt Modal Component -->
  <ReceiptModal
      ref="receiptModal"
      :member-info="memberInfo"
      :total-beneficiaries="totalBeneficiaries"
      :expected-annual-premium="annualPremium"
      :total-paid-for-year="totalPaidThisYear"
  />

  <!-- Delete Member Modal Component -->
  <DeleteMemberModal
      ref="deleteMemberModal"
      :cbhi-number="cbhiNumber"
      :member-info="memberInfo"
      :beneficiary-count="beneficiaries.length"
      @member-deleted="handleMemberDeleted"
  />

  <!-- Suspend Member Modal Component -->
  <SuspendMemberModal
      ref="suspendMemberModal"
      :cbhi-number="cbhiNumber"
      :member-info="memberInfo"
      :beneficiary-count="beneficiaries.length"
      @member-suspended="handleMemberSuspended"
      @suspension-error="handleSuspensionError"
  />

  <!-- Reactivate Member Modal Component -->
  <ReactivateMemberModal
      ref="reactivateMemberModal"
      :cbhi-number="cbhiNumber"
      :member-info="memberInfo"
      :beneficiary-count="beneficiaries.length"
      @member-reactivated="handleMemberReactivated"
      @reactivation-error="handleReactivationError"
  />

  <!-- NEW: Plan Change Modal Component -->
  <PlanChangeModal
      ref="planChangeModal"
      :member-id="memberId"
      :member-info="memberInfo"
      :current-plan="selectedPlan"
      :total-beneficiaries="totalBeneficiaries"
      @plan-changed="handlePlanChanged"
      @plan-change-error="handlePlanChangeError"
  />

  <!-- Member Photo Update Modal Component -->
  <MemberPhotoModal
      ref="memberPhotoModal"
      :member-info="memberInfo"
      @photo-updated="handlePhotoUpdated"
      @photo-error="handlePhotoError"
  />

  <!-- Photo View Modal Component -->
  <PhotoViewModal
      ref="photoViewModal"
      :member-info="memberInfo"
      :photo-info="photoInfo"
      @photo-downloaded="handlePhotoDownloaded"
      @download-error="handleDownloadError"
      @url-copied="handleUrlCopied"
      @copy-error="handleCopyError"
  />

  <BeneficiaryPhotoModal
      ref="beneficiaryPhotoModal"
      :cbhi-number="cbhiNumber"
      @photo-updated="handleBeneficiaryPhotoUpdated"
      @photo-error="handleBeneficiaryPhotoError"
  />

  <BeneficiaryDetailsModal
      ref="beneficiaryDetailsModal"
      :cbhi-number="cbhiNumber"
      :member-info="memberInfo"
      :selected-plan="selectedPlan"
      :beneficiary-index="0"
      @view-photo="viewBeneficiaryPhoto"
      @edit-beneficiary="editBeneficiary"
      @update-photo="openBeneficiaryPhotoModal"
      @remove-beneficiary="removeBeneficiary"
  />

  <!-- Right bar overlay-->
  <div class="rightbar-overlay"></div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import BeneficiaryModal from "@/components/cbhi-member-management/modals/BeneficiaryModal.vue";
import PaymentModal from "@/components/cbhi-member-management/modals/PaymentModal.vue";
import ReceiptModal from "@/components/cbhi-member-management/modals/ReceiptModal.vue";
import DeleteMemberModal from "@/components/cbhi-member-management/modals/DeleteMemberModal.vue";
import SuspendMemberModal from "@/components/cbhi-member-management/modals/SuspendMemberModal.vue";
import ReactivateMemberModal from "@/components/cbhi-member-management/modals/ReactivateMemberModal.vue";
import PlanChangeModal from "@/components/cbhi-member-management/modals/PlanChangeModal.vue";
import MemberPhotoModal from "@/components/cbhi-member-management/modals/MemberPhotoModal.vue";
import PhotoViewModal from "@/components/cbhi-member-management/modals/PhotoViewModal.vue";
import BeneficiaryPhotoModal from "@/components/cbhi-member-management/modals/BeneficiaryPhotoModal.vue";
import BeneficiaryDetailsModal from "@/components/cbhi-member-management/modals/BeneficiaryDetailsModal.vue";

import { authUrl, executeJson } from "@/api";
import router from "@/router";

export default {
  name: "MemberDetails",
  components: {
    NavigationBar,
    BeneficiaryModal,
    PaymentModal,
    ReceiptModal,
    DeleteMemberModal,
    SuspendMemberModal,
    ReactivateMemberModal,
    PlanChangeModal,
    MemberPhotoModal,
    PhotoViewModal,
    BeneficiaryPhotoModal,
    BeneficiaryDetailsModal
  },
  data() {
    return {
      // Loading state
      loading: false,

      // Member identifier - using cbhiNumber instead of memberId
      cbhiNumber: null,

      // Member data
      memberInfo: {},
      memberDetails: null,
      beneficiaries: [],
      payments: [],
      claims: [],
      hospitalVisits: [],
      suspensionHistory: [],
      photoInfo: null,
      currentStatus: {
        isActive: false,
        daysUntilExpiry: 0,
        totalBeneficiaries: 0,
        totalClaims: 0,
        totalPaidPremiums: 0
      },

      // Plan-based system
      selectedPlan: null,
      planDetails: null,
      planLoading: false,
      availablePlans: [],

      // Alert states
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: ''
    };
  },

  computed: {
    isCardIssued() {
      return this.memberInfo.cbhiCardIssued === 1 ||
          this.memberInfo.cbhiCardIssued === '1' ||
          this.memberInfo.cbhi_card_issued === 1;
    },

    memberFullName() {
      return this.memberInfo.fullName || this.memberInfo.full_name || 'Unknown Member';
    },

    hasProfilePhoto() {
      return this.photoInfo && this.photoInfo.hasPhoto &&
          (this.memberInfo.profile_photo || this.photoInfo.photoPath);
    },

    isMemberDeleted() {
      return this.memberInfo && this.memberInfo.status === 'deleted';
    },

    isMemberSuspended() {
      return this.memberInfo && (this.memberInfo.status === 'suspended' || this.memberInfo.status === 'archived');
    },

    isPermanentlyArchived() {
      return this.memberInfo && this.memberInfo.status === 'archived' && this.memberInfo.suspension_type === 'permanent';
    },

    hasDeletionInfo() {
      return this.memberInfo && this.memberInfo.deletion_date && this.memberInfo.deletion_reason;
    },

    hasSuspensionHistory() {
      return this.suspensionHistory && this.suspensionHistory.length > 0;
    },

    memberStatusBadgeClass() {
      if (!this.memberInfo.status) return 'badge-soft-secondary';

      const statusMap = {
        'active': 'badge-soft-success',
        'inactive': 'badge-soft-warning',
        'suspended': 'badge-soft-warning',
        'archived': 'badge-soft-danger',
        'deleted': 'badge-soft-danger',
        'expired': 'badge-soft-secondary'
      };

      return statusMap[this.memberInfo.status] || 'badge-soft-secondary';
    },

    totalBeneficiaries() {
      return 1 + this.beneficiaries.length;
    },

    annualPremium() {
      return this.memberDetails?.financialSummary?.annualPremium || 0;
    },

    totalPaidThisYear() {
      return this.memberDetails?.financialSummary?.totalPaidThisYear || 0;
    },

    remainingBalance() {
      return this.memberDetails?.financialSummary?.remainingBalance || 0;
    },

    paymentStatusFromAPI() {
      return this.memberDetails?.financialSummary?.paymentStatus || 'PAYMENT_DUE';
    },

    totalClaimsAmount() {
      return this.claims.reduce((sum, claim) => sum + (parseFloat(claim.total_amount) || 0), 0);
    },

    averageClaimAmount() {
      if (this.claims.length === 0) return 0;
      return Math.round(this.totalClaimsAmount / this.claims.length);
    },

    lastVisitDate() {
      if (this.hospitalVisits.length === 0) return null;
      const dates = this.hospitalVisits.map(v => new Date(v.service_date));
      const maxDate = new Date(Math.max(...dates));
      return maxDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    },

    paymentStatus() {
      return this.paymentStatusFromAPI === 'PAID_IN_FULL' ? 'Up to Date' : 'Payment Due';
    },

    paymentStatusText() {
      if (this.remainingBalance <= 0) {
        return 'Fully Paid';
      }
      return `Due: ETB ${this.formatCurrency(this.remainingBalance)}`;
    },

    paymentStatusClass() {
      if (this.remainingBalance <= 0) {
        return 'text-success';
      }
      return 'text-warning';
    },

    yearsCovered() {
      if (!this.memberInfo.registrationDate) return 0;
      const registrationYear = new Date(this.memberInfo.registrationDate).getFullYear();
      const currentYear = new Date().getFullYear();
      return currentYear - registrationYear + 1;
    },

    // ✅ NEW: Get adult child age threshold from plan
    adultChildAgeThreshold() {
      return this.selectedPlan?.adult_child_age_threshold || 18;
    },

    // ✅ NEW: Get free spouses count from plan
    freeSpousesCount() {
      return this.selectedPlan?.free_spouses_count || 1;
    },

    // ✅ NEW: Get additional member rate from plan
    additionalMemberRate() {
      return this.selectedPlan?.additional_member_rate || 0.20;
    }
  },

  mounted() {
    this.cbhiNumber = this.$route.params.cbhi_number;
    const storedMember = sessionStorage.getItem('memberCard');
    if (storedMember) {
      const memberData = JSON.parse(storedMember);
      if (memberData.cbhi_number === this.cbhiNumber) {
        this.memberInfo = memberData;
      }
    }

    this.loadMemberDetails();
    this.loadSuspensionHistory();
    this.loadAvailablePlans();
  },

  methods: {
    // ✅ NEW: Get plan free spouses text
    getPlanFreeSpousesText() {
      const count = this.selectedPlan?.free_spouses_count;
      if (count === 999 || count === null || count === undefined) {
        return 'Unlimited';
      }
      return count;
    },

    // ✅ NEW: Get plan adult child threshold
    getPlanAdultChildThreshold() {
      const threshold = this.selectedPlan?.adult_child_age_threshold;
      if (threshold === 999 || threshold === null || threshold === undefined) {
        return 'All Ages';
      }
      return threshold;
    },

    // ✅ NEW: Get plan additional member rate
    getPlanAdditionalMemberRate() {
      const rate = this.selectedPlan?.additional_member_rate || 0.20;
      return `${(rate * 100).toFixed(0)}%`;
    },

    // ✅ NEW: Get additional member fee
    getAdditionalMemberFee() {
      if (!this.selectedPlan) return 0;
      const primaryAmount = this.selectedPlan.primary_annual_amount || 0;
      const rate = this.selectedPlan.additional_member_rate || 0.20;
      return primaryAmount * rate;
    },

    // ✅ NEW: Check if beneficiary is a free spouse
    isBeneficiaryFreeSpouse(beneficiary, index) {
      const relationship = (beneficiary.relationship || '').toLowerCase();
      if (!['spouse', 'husband', 'wife'].includes(relationship)) {
        return false;
      }

      // Count spouses before this one
      let spouseIndex = 0;
      for (let i = 0; i < index; i++) {
        const rel = (this.beneficiaries[i].relationship || '').toLowerCase();
        if (['spouse', 'husband', 'wife'].includes(rel) && this.beneficiaries[i].status === 'active') {
          spouseIndex++;
        }
      }

      const freeCount = this.selectedPlan?.free_spouses_count || 1;
      if (freeCount === 999) return true; // Unlimited
      return spouseIndex < freeCount;
    },

    // ✅ NEW: Check if beneficiary is a free child (under threshold)
    isBeneficiaryFreeChild(beneficiary) {
      const relationship = (beneficiary.relationship || '').toLowerCase();
      if (!['child', 'son', 'daughter'].includes(relationship)) {
        return false;
      }

      const age = this.calculateAge(beneficiary.date_of_birth || beneficiary.dateOfBirth);
      const threshold = this.selectedPlan?.adult_child_age_threshold || 18;

      if (threshold === 999) return true; // All ages free
      return age < threshold;
    },

    // ✅ NEW: Check if beneficiary qualifies for free coverage due to disability
    isBeneficiaryFreeDisability(beneficiary) {
      const relationship = (beneficiary.relationship || '').toLowerCase();
      if (!['child', 'son', 'daughter'].includes(relationship)) {
        return false;
      }

      const age = this.calculateAge(beneficiary.date_of_birth || beneficiary.dateOfBirth);
      const threshold = this.selectedPlan?.adult_child_age_threshold || 18;

      // Only applies to adult children (18+)
      if (age < threshold) {
        return false;
      }

      // Check disability flag
      return beneficiary.hasDisability || beneficiary.has_disability || false;
    },

    // ✅ NEW: Check if beneficiary qualifies for free coverage due to dependency
    isBeneficiaryFreeDependency(beneficiary) {
      const relationship = (beneficiary.relationship || '').toLowerCase();
      if (!['child', 'son', 'daughter'].includes(relationship)) {
        return false;
      }

      const age = this.calculateAge(beneficiary.date_of_birth || beneficiary.dateOfBirth);
      const threshold = this.selectedPlan?.adult_child_age_threshold || 18;

      // Only applies to adult children (18+)
      if (age < threshold) {
        return false;
      }

      // Check if already covered by disability
      if (beneficiary.hasDisability || beneficiary.has_disability) {
        return false;
      }

      // Check dependency flag
      return beneficiary.isDependent || beneficiary.is_dependent || false;
    },

    // ✅ NEW: Count free beneficiaries
    countFreeBeneficiaries() {
      let count = 0;
      this.beneficiaries.forEach((ben, index) => {
        if (ben.status !== 'active') return;
        if (this.isBeneficiaryFreeSpouse(ben, index) ||
            this.isBeneficiaryFreeChild(ben) ||
            this.isBeneficiaryFreeDisability(ben) ||
            this.isBeneficiaryFreeDependency(ben)) {
          count++;
        }
      });
      return count;
    },

    // ✅ NEW: Count disability beneficiaries
    countDisabilityBeneficiaries() {
      return this.beneficiaries.filter(ben =>
          ben.status === 'active' &&
          (ben.hasDisability || ben.has_disability)
      ).length;
    },

    // ✅ NEW: Count dependent beneficiaries
    countDependentBeneficiaries() {
      return this.beneficiaries.filter(ben =>
          ben.status === 'active' &&
          (ben.isDependent || ben.is_dependent) &&
          !(ben.hasDisability || ben.has_disability)
      ).length;
    },

    // ✅ NEW: Count paid beneficiaries
    countPaidBeneficiaries() {
      let count = 0;
      this.beneficiaries.forEach((ben, index) => {
        if (ben.status !== 'active') return;
        if (!this.isBeneficiaryFreeSpouse(ben, index) &&
            !this.isBeneficiaryFreeChild(ben) &&
            !this.isBeneficiaryFreeDisability(ben) &&
            !this.isBeneficiaryFreeDependency(ben)) {
          count++;
        }
      });
      return count;
    },

    viewBeneficiaryPhoto(beneficiary) {
      if (!this.beneficiaryHasPhoto(beneficiary)) {
        this.openBeneficiaryPhotoModal(beneficiary);
        return;
      }

      const photoUrl = this.getBeneficiaryPhotoUrl(beneficiary);
      this.$refs.photoViewModal.openModalForBeneficiary(beneficiary, photoUrl);
    },

    onBeneficiaryImageError(event, beneficiary) {
      console.error('Failed to load beneficiary image:', event.target.src);
      event.target.style.display = 'none';
      const index = this.beneficiaries.findIndex(b => b.beneficiary_id === beneficiary.beneficiary_id);
      if (index !== -1) {
        this.beneficiaries[index].profile_photo = null;
      }
    },

    openBeneficiaryPhotoModal(beneficiary) {
      if (this.isMemberDeleted) {
        this.showAlert('failed', 'Cannot update photos for deleted member records');
        return;
      }
      if (this.isMemberSuspended) {
        this.showAlert('failed', 'Cannot update photos for suspended member records');
        return;
      }
      this.$refs.beneficiaryPhotoModal.openModal(beneficiary);
    },

    handleBeneficiaryPhotoUpdated(event) {
      this.showAlert('success', event.message || 'Beneficiary photo updated successfully');

      const beneficiaryIndex = this.beneficiaries.findIndex(b => b.beneficiary_id === event.beneficiaryId);
      if (beneficiaryIndex !== -1) {
        this.beneficiaries[beneficiaryIndex].profile_photo = event.photoPath;
      }

      setTimeout(() => {
        this.loadMemberDetails();
      }, 1000);
    },

    handleBeneficiaryPhotoError(event) {
      this.showAlert('failed', event.message || 'Error updating beneficiary photo');
    },

    getBeneficiaryPhotoUrl(beneficiary) {
      if (beneficiary.profile_photo) {
        if (beneficiary.profile_photo.startsWith('http')) {
          return beneficiary.profile_photo;
        }
        return `https://rcmsuat.craftsilicon.com/api1/cbhi//uploads/beneficiary-photos/${beneficiary.profile_photo}`;
      }
      return null;
    },

    beneficiaryHasPhoto(beneficiary) {
      return beneficiary && beneficiary.profile_photo;
    },

    getPhotoUrl() {
      if (this.photoInfo && this.photoInfo.photoUrl) {
        return this.photoInfo.photoUrl;
      }

      if (this.photoInfo && this.photoInfo.photoPath) {
        if (this.photoInfo.photoPath.startsWith('http')) {
          return this.photoInfo.photoPath;
        }
        return `https://rcmsuat.craftsilicon.com/api1/cbhi//uploads/member-photos/${this.photoInfo.photoPath}`;
      }

      if (this.memberInfo.profile_photo) {
        if (this.memberInfo.profile_photo.startsWith('http')) {
          return this.memberInfo.profile_photo;
        }
        return `https://rcmsuat.craftsilicon.com/api1/cbhi//uploads/member-photos/${this.memberInfo.profile_photo}`;
      }

      return '';
    },

    onImageError(event) {
      console.error('Failed to load image:', event.target.src);
      event.target.style.display = 'none';
      const defaultAvatar = event.target.nextElementSibling;
      if (defaultAvatar && defaultAvatar.classList.contains('bg-primary')) {
        defaultAvatar.style.display = 'flex';
      }
    },

    handlePhotoUpdated(event) {
      this.showAlert('success', event.message || 'Member photo updated successfully');

      if (event.photoPath) {
        this.memberInfo.profile_photo = event.photoPath;
        this.memberInfo.photo_upload_date = new Date().toISOString();

        if (this.photoInfo) {
          this.photoInfo.photoPath = event.photoPath;
          this.photoInfo.hasPhoto = true;
          this.photoInfo.uploadDate = new Date().toISOString();
          this.photoInfo.photoUrl = `https://rcmsuat.craftsilicon.com/api1/uploads/member-photos/${event.photoPath}`;
        }
      }

      setTimeout(() => {
        this.loadMemberDetails();
      }, 1000);
    },

    handlePhotoError(event) {
      this.showAlert('failed', event.message || 'Error updating member photo');
    },

    async loadAvailablePlans() {
      const data = {
        "function": "getAvailablePlans"
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.availablePlans = res.data.data.plans || [];
        }
      } catch (e) {
        console.error("Failed to load available plans:", e);
      }
    },

    showPlanChangeModal() {
      if (this.isMemberDeleted) {
        this.showAlert('failed', 'Cannot change plans for deleted member records');
        return;
      }
      if (this.isMemberSuspended) {
        this.showAlert('failed', 'Cannot change plans for suspended member records');
        return;
      }
      this.$refs.planChangeModal.openModal();
    },

    handlePlanChanged(event) {
      this.showAlert('success', event.message || 'Plan changed successfully');
      this.loadMemberDetails();
    },

    handlePlanChangeError(event) {
      this.showAlert('failed', event.message || 'Error changing plan');
    },

    getPlanTypeBadgeClass(planType) {
      const typeClasses = {
        'basic': 'bg-info',
        'standard': 'bg-primary',
        'premium': 'bg-success',
        'family': 'bg-warning text-dark'
      };
      return typeClasses[planType.toLowerCase()] || 'bg-secondary';
    },

    groupServicesByCategory(services) {
      if (!services || !Array.isArray(services)) return {};

      return services.reduce((groups, service) => {
        const category = service.category || 'Other Services';
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(service);
        return groups;
      }, {});
    },

    suspendMember() {
      if (this.isMemberDeleted) {
        this.showAlert('info', 'Cannot suspend deleted member records');
        return;
      }

      if (this.isMemberSuspended) {
        this.$refs.reactivateMemberModal.openModal();
      } else {
        this.$refs.suspendMemberModal.openModal();
      }
    },

    handleMemberSuspended(event) {
      if (event.success) {
        this.showAlert('success', event.message || 'Member suspended successfully');
        this.loadMemberDetails();
        this.loadSuspensionHistory();
      }
    },

    handleSuspensionError(event) {
      this.showAlert('failed', event.message || 'Error during suspension process');
    },

    handleMemberReactivated(event) {
      if (event.success) {
        this.showAlert('success', event.message || 'Member reactivated successfully');
        this.loadMemberDetails();
        this.loadSuspensionHistory();
      }
    },

    handleReactivationError(event) {
      this.showAlert('failed', event.message || 'Error during reactivation process');
    },

    async loadSuspensionHistory() {
      const data = {
        "function": "getMemberSuspensionHistory",
        "cbhiNumber": this.cbhiNumber
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.suspensionHistory = res.data.data.suspensionHistory || [];
        }
      } catch (e) {
        console.error("Failed to load suspension history:", e);
      }
    },

    viewBeneficiaryDetails(beneficiary) {
      this.$refs.beneficiaryDetailsModal.openModal(beneficiary);
    },

    formatSuspensionCategory(category) {
      if (!category) return 'Unknown';

      const categoryMap = {
        'non_payment': 'Non-Payment of Premiums',
        'fraud': 'Suspected Fraud',
        'deceased': 'Member Deceased',
        'relocated': 'Member Relocated',
        'duplicate': 'Duplicate Registration',
        'request': 'Member Request',
        'eligibility': 'No Longer Eligible',
        'violation': 'Policy Violation',
        'administrative': 'Administrative Action',
        'other': 'Other Reason'
      };

      return categoryMap[category] || category;
    },

    getMemberStatusDisplay() {
      if (!this.memberInfo.status) return 'UNKNOWN';

      if (this.memberInfo.status === 'deleted') return 'DELETED';
      if (this.memberInfo.status === 'suspended') return 'SUSPENDED';
      if (this.memberInfo.status === 'archived') return 'ARCHIVED';

      return this.memberInfo.status.toUpperCase();
    },

    getSuspendButtonClass() {
      if (this.isMemberSuspended) {
        return 'btn btn-success waves-effect';
      }
      return 'btn btn-outline-warning waves-effect';
    },

    getSuspendButtonIcon() {
      if (this.isMemberSuspended) {
        return 'bx bx-play-circle me-2';
      }
      return 'bx bx-pause-circle me-2';
    },

    getSuspendButtonText() {
      if (this.isPermanentlyArchived) {
        return 'Archived';
      }
      if (this.isMemberSuspended) {
        return 'Reactivate';
      }
      return 'Suspend';
    },

    getEditButtonClass() {
      if (this.isMemberDeleted || this.isMemberSuspended) {
        return 'btn btn-secondary btn-sm waves-effect waves-light disabled';
      }
      return 'btn btn-outline-primary btn-sm waves-effect waves-light';
    },

    getEditButtonText() {
      if (this.isMemberDeleted) return 'Archived';
      if (this.isMemberSuspended) return 'Suspended';
      return 'Edit Profile';
    },

    getEditButtonTooltip() {
      if (this.isMemberDeleted) return 'Cannot edit deleted member';
      if (this.isMemberSuspended) return 'Cannot edit suspended member';
      return 'Edit member profile';
    },

    getAnnualPremiumLabel() {
      if (this.isMemberDeleted) return 'Coverage Terminated';
      if (this.isMemberSuspended) return 'Coverage Suspended';
      return `Annual Premium (${this.totalBeneficiaries} Members)`;
    },

    getPaymentStatusLabel() {
      if (this.isMemberDeleted) return 'Final Paid Amount';
      if (this.isMemberSuspended) return 'Payments Suspended';
      return 'Paid to date';
    },

    getClaimsLabel() {
      if (this.isMemberDeleted) return 'Total Benefits Used (Archived)';
      if (this.isMemberSuspended) return 'Benefits Suspended';
      return 'Total Benefits Consumed';
    },

    getMemberStatusAlertClass() {
      let baseClass = 'alert border p-3 d-flex alert-soft-info mb-0 border border-dashed';

      if (this.isMemberDeleted) {
        return baseClass.replace('alert-soft-info', 'alert-soft-danger').replace('border-info', 'border-danger');
      }
      if (this.isMemberSuspended) {
        return baseClass.replace('alert-soft-info', 'alert-soft-warning').replace('border-info', 'border-warning');
      }

      return baseClass + ' alert-info border-info';
    },

    getMemberStatusIcon() {
      if (this.isMemberDeleted) return 'bx bx-trash fs-1 text-danger';
      if (this.isMemberSuspended) return 'bx bx-pause-circle fs-1 text-warning';
      return 'bx bx-user-check fs-1 text-info';
    },

    getStatusBadgeClass() {
      if (this.isMemberDeleted) return 'bg-danger';
      if (this.isMemberSuspended) return 'bg-warning';
      if (this.currentStatus.isActive) return 'bg-success';
      return 'bg-danger';
    },

    getStatusText() {
      if (this.isMemberDeleted) return 'Deleted';
      if (this.isMemberSuspended) return 'Suspended';
      if (this.currentStatus.isActive) return 'Active';
      return 'Inactive';
    },

    getBeneficiariesTitle() {
      if (this.isMemberDeleted) return 'Former Beneficiaries (Archived)';
      if (this.isMemberSuspended) return `Suspended Beneficiaries (${this.totalBeneficiaries})`;
      return `Covered Beneficiaries (${this.totalBeneficiaries})`;
    },

    getBeneficiaryStatusBadgeClass(beneficiary) {
      const statusMap = {
        'active': 'bg-success',
        'inactive': 'bg-warning',
        'suspended': 'bg-warning',
        'deleted': 'bg-danger'
      };

      return statusMap[beneficiary.status] || 'bg-secondary';
    },

    getBeneficiaryStatusText(beneficiary) {
      if (beneficiary.status === 'deleted') return 'Deleted';
      if (beneficiary.status === 'suspended') return 'Suspended';
      return this.capitalizeFirst(beneficiary.status || 'unknown');
    },

    getInactiveBeneficiaryButtonText(beneficiary) {
      if (this.isMemberDeleted) return 'Archived';
      if (this.isMemberSuspended) return 'Suspended';
      if (beneficiary.status === 'deleted') return 'Deleted';
      return 'Inactive';
    },

    getNoBeneficiariesMessage() {
      if (this.isMemberDeleted) return 'No beneficiaries were registered before deletion';
      if (this.isMemberSuspended) return 'Beneficiaries are suspended';
      return 'No additional beneficiaries added yet';
    },

    getNoPaymentsMessage() {
      if (this.isMemberDeleted) return 'No payments were recorded for this member';
      if (this.isMemberSuspended) return 'Payments suspended for this member';
      return 'No payment history available';
    },

    getClaimStatusBadgeClass(claim) {
      const statusMap = {
        'approved': 'bg-success',
        'paid': 'bg-success',
        'submitted': 'bg-warning',
        'pending': 'bg-warning',
        'processing': 'bg-info',
        'rejected': 'bg-danger',
        'on_hold': 'bg-secondary'
      };

      return statusMap[claim.status] || 'bg-secondary';
    },

    getClaimStatusIcon(claim) {
      if (claim.status === 'approved' || claim.status === 'paid') {
        return 'mdi mdi-check me-1';
      }
      if (claim.status === 'on_hold') {
        return 'bx bx-pause-circle me-1';
      }
      return '';
    },

    getPaymentStatusBadgeClass(payment) {
      const statusMap = {
        'completed': 'bg-success',
        'pending': 'bg-warning',
        'failed': 'bg-danger',
        'reversed': 'bg-secondary'
      };

      return statusMap[payment.status] || 'bg-secondary';
    },

    deleteMember() {
      if (this.isMemberDeleted) {
        this.showAlert('info', 'This member record is already deleted');
        return;
      }

      if (this.isMemberSuspended) {
        this.showAlert('info', 'Please reactivate the member before deletion');
        return;
      }

      this.$refs.deleteMemberModal.openModal();
    },

    handleMemberDeleted(event) {
      if (event.success) {
        this.showAlert('success', event.message || 'Member deleted successfully. Redirecting to members list...');

        setTimeout(() => {
          router.push('/members-all');
        }, 3000);
      } else {
        this.showAlert('failed', event.message || 'Failed to delete member');
      }
    },

    async loadPlanDetails() {
      if (!this.memberInfo.selectedPlanId) {
        console.log('No plan selected for this member');
        return;
      }

      this.planLoading = true;

      const data = {
        "function": "getPlanDetails",
        "planId": this.memberInfo.selectedPlanId
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.selectedPlan = res.data.data.plan;
          this.planDetails = res.data.data.plan;
          console.log('Plan loaded:', this.selectedPlan);
        } else {
          console.error('Failed to load plan details:', res.data.message);
          this.showAlert('failed', 'Failed to load plan details');
        }
      } catch (e) {
        console.error("Failed to load plan details:", e);
        this.showAlert('failed', 'Error loading plan details');
      } finally {
        this.planLoading = false;
      }
    },

    editMember() {
      if (this.isMemberDeleted) {
        this.showAlert('failed', 'Cannot edit deleted member records');
        return;
      }
      if (this.isMemberSuspended) {
        this.showAlert('failed', 'Cannot edit suspended member records');
        return;
      }
      router.push({
        name: 'members-edit',
        params: { cbhi_number: this.cbhiNumber }
      });
    },

    viewCard() {
      if (this.isMemberDeleted) {
        this.showAlert('failed', 'Cannot view card for deleted member records');
        return;
      }
      if (this.isMemberSuspended) {
        this.showAlert('failed', 'Cannot view card for suspended member records');
        return;
      }

      const memberCardData = {
        cbhi_number: this.memberInfo.cbhiNumber || this.memberInfo.cbhi_number,
        fullName: this.memberFullName,
        dateOfBirth: this.memberInfo.dateOfBirth,
        gender: this.memberInfo.gender,
        profilePhoto: this.memberInfo.profile_photo,
        regionName: this.memberInfo.regionName,
        woredaName: this.memberInfo.woredaName,
        expiryDate: this.memberInfo.expiryDate,
        status: this.memberInfo.status,
        cbhiCardIssued: this.memberInfo.cbhiCardIssued,
        cardIssueDate: this.memberInfo.cardIssueDate,
        cardIssuedBy: this.memberInfo.cardIssuedBy,
        cardIssueNotes: this.memberInfo.cardIssueNotes
      };

      sessionStorage.setItem('memberCard', JSON.stringify(memberCardData));

      router.push({
        name: 'member-card',
        params: {
          cbhi_number: this.cbhiNumber
        }
      });
    },

    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    },

    async loadMemberDetails() {
      this.loading = true;

      const data = {
        "function": "getMemberDetails",
        "cbhiNumber": this.cbhiNumber
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          const details = res.data.data;

          this.memberDetails = details;

          this.memberInfo = {
            memberId: details.memberInfo?.memberId,
            cbhiNumber: details.memberInfo?.cbhiNumber,
            memberCode: details.memberInfo?.memberCode,
            title: details.memberInfo?.title,
            fullName: details.memberInfo?.fullName || details.memberInfo?.full_name || "",
            grandfatherName: details.memberInfo?.grandfatherName,
            dateOfBirth: details.memberInfo?.dateOfBirth,
            age: details.memberInfo?.age,
            gender: details.memberInfo?.gender,
            nationalId: details.memberInfo?.nationalId,
            maritalStatus: details.memberInfo?.maritalStatus,
            occupation: details.memberInfo?.occupation,
            monthlyIncome: details.memberInfo?.monthlyIncome,
            phoneNumber: details.memberInfo?.phoneNumber,
            emailAddress: details.memberInfo?.emailAddress,
            address: details.memberInfo?.address,
            membershipType: details.memberInfo?.membershipType,
            enrollmentType: details.memberInfo?.enrollmentType,
            relationshipToHead: details.memberInfo?.relationshipToHead,
            hewRegistered: details.memberInfo?.hewRegistered,
            registrationDate: details.membershipStatus?.registrationDate,
            renewalDate: details.membershipStatus?.renewalDate,
            expiryDate: details.membershipStatus?.expiryDate,
            status: details.membershipStatus?.status,
            suspensionDate: details.membershipStatus?.suspensionInfo?.suspensionDate,
            suspensionType: details.membershipStatus?.suspensionInfo?.suspensionType,
            suspensionCategory: details.membershipStatus?.suspensionInfo?.suspensionCategory,
            suspensionReason: details.membershipStatus?.suspensionInfo?.suspensionReason,
            expectedReactivationDate: details.membershipStatus?.suspensionInfo?.expectedReactivationDate,
            selectedPlanId: details.planInfo?.planId,
            regionName: details.memberInfo?.location?.region,
            zoneName: details.memberInfo?.location?.zone,
            woredaName: details.memberInfo?.location?.woreda,
            kebeleName: details.memberInfo?.location?.kebele,
            householdSize: details.householdInfo?.householdSize,
            profile_photo: details.memberInfo?.profilePhoto,
            photo_upload_date: details.memberInfo?.photoUploadDate,
            photo_uploaded_by: details.memberInfo?.photoUploadedBy,
            photo_uploaded_by_username: details.memberInfo?.photoUploadedByUsername,
            cbhiCardIssued: details.cardInfo?.cbhiCardIssued || details.memberInfo?.cbhiCardIssued || details.memberInfo?.cbhi_card_issued,
            cardIssueDate: details.cardInfo?.cardIssueDate || details.memberInfo?.cardIssueDate || details.memberInfo?.card_issue_date,
            cardIssuedBy: details.cardInfo?.cardIssuedBy || details.memberInfo?.cardIssuedBy || details.memberInfo?.card_issued_by,
            cardIssueNotes: details.cardInfo?.cardIssueNotes || details.memberInfo?.cardIssueNotes || details.memberInfo?.card_issue_notes
          };

          this.photoInfo = details.photoInfo || null;

          // ✅ UPDATED: Map beneficiaries with disability/dependency fields
          this.beneficiaries = (details.householdInfo?.beneficiaries || []).map(ben => ({
            ...ben,
            // Ensure disability and dependency fields are properly mapped
            hasDisability: ben.hasDisability || ben.has_disability || false,
            disabilityNotes: ben.disabilityNotes || ben.disability_notes || null,
            isDependent: ben.isDependent || ben.is_dependent || false,
            dependencyNotes: ben.dependencyNotes || ben.dependency_notes || null
          }));

          this.payments = details.recentActivity?.payments || [];
          this.claims = details.recentActivity?.claims || [];
          this.hospitalVisits = [];

          this.currentStatus = {
            isActive: details.membershipStatus?.isActive || false,
            daysUntilExpiry: details.membershipStatus?.daysUntilExpiry || 0,
            totalBeneficiaries: details.householdInfo?.totalBeneficiaries || 0,
            totalClaims: details.coverageUtilization?.totalClaimsThisYear || 0,
            totalPaidPremiums: details.financialSummary?.totalPaidThisYear || 0
          };

          // ✅ UPDATED: Set plan information with all premium-related fields
          if (details.planInfo && details.planInfo.planId) {
            this.selectedPlan = {
              plan_id: details.planInfo.planId,
              plan_name: details.planInfo.planName,
              plan_code: details.planInfo.planCode,
              plan_type: details.planInfo.planType,
              plan_description: details.planInfo.planDescription,
              primary_annual_amount: details.planInfo.premiumRates?.primaryRate || 0,
              dependent_annual_amount: details.planInfo.premiumRates?.dependentRate || 0,
              additional_member_rate: details.planInfo.premiumRates?.additionalMemberRate || 0.20,
              max_free_dependents: details.planInfo.premiumRates?.maxFreeDependents || 0,
              free_spouses_count: details.planInfo.premiumRates?.freeSpousesCount || 1,
              free_children_under_18: details.planInfo.premiumRates?.freeChildrenUnder18 || 1,
              adult_child_age_threshold: details.planInfo.premiumRates?.adultChildAgeThreshold || 18,
              annual_coverage_limit: details.planInfo.annualCoverageLimit || 0,
              per_visit_limit: details.planInfo.perVisitLimit || 0,
              benefits: details.planInfo.benefits || null,
              coverage_configs: details.planInfo.coverage_configs || null
            };
          }

          console.log('Member Info loaded:', this.memberInfo);
          console.log('Beneficiaries loaded:', this.beneficiaries);
          console.log('Selected Plan loaded:', this.selectedPlan);
          console.log('Photo Info loaded:', this.photoInfo);

        } else {
          this.showAlert('failed', res.data.message || 'Failed to load member details');
        }
      } catch (e) {
        console.error('Error loading member details:', e);
        this.showAlert('failed', e.message || 'Failed to load member details');
      } finally {
        this.loading = false;
      }
    },

    switchTab(tabName) {
      const tabElement = document.querySelector(`a[href="#${tabName}"]`);
      if (tabElement && typeof bootstrap !== 'undefined') {
        const tab = new bootstrap.Tab(tabElement);
        tab.show();
      }
    },

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return 0;
      const dob = new Date(dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
      }

      return age;
    },

    getInitials(person) {
      if (!person || !person.fullName) return 'NA';
      const names = (person.fullName || person.full_name || '').trim().split(' ');
      if (names.length === 1) return names[0].charAt(0).toUpperCase();
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    },

    getBeneficiaryInitials(beneficiary) {
      const fullName = beneficiary.full_name || beneficiary.fullName || '';
      const names = fullName.trim().split(' ');
      if (names.length === 1) return names[0].charAt(0).toUpperCase();
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    },

    getBeneficiaryFullName(beneficiary) {
      return beneficiary.full_name || beneficiary.fullName || 'Unknown';
    },

    getClaimInitials(claim) {
      if (!claim.beneficiary_id) {
        return (this.memberInfo.fullName?.charAt(0) || '?').toUpperCase();
      }

      const beneficiary = this.beneficiaries.find(b => b.beneficiary_id === claim.beneficiary_id);
      if (beneficiary) {
        const fullName = beneficiary.full_name || beneficiary.fullName || '';
        const names = fullName.trim().split(' ');
        if (names.length === 1) return names[0].charAt(0).toUpperCase();
        return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
      }

      return 'NA';
    },

    getClaimantName(claim) {
      if (!claim.beneficiary_id) {
        return this.memberFullName;
      }

      const beneficiary = this.beneficiaries.find(b => b.beneficiary_id === claim.beneficiary_id);
      return beneficiary ? (beneficiary.full_name || beneficiary.fullName || 'Unknown') : 'Unknown';
    },

    getClaimantRelationship(claim) {
      if (!claim.beneficiary_id) {
        return `Self (${this.calculateAge(this.memberInfo.dateOfBirth)} years)`;
      }

      const beneficiary = this.beneficiaries.find(b => b.beneficiary_id === claim.beneficiary_id);
      if (beneficiary) {
        return `${this.capitalizeFirst(beneficiary.relationship)} (${this.calculateAge(beneficiary.date_of_birth)} years)`;
      }

      return 'Unknown';
    },

    getPaymentMethodIcon(method) {
      const methodLower = (method || '').toLowerCase();
      if (methodLower.includes('telebirr') || methodLower.includes('mpesa') || methodLower.includes('cbe')) {
        return 'bx bx-mobile-alt text-success';
      }
      if (methodLower.includes('bank')) {
        return 'bx bx-building-house text-primary';
      }
      if (methodLower.includes('cash')) {
        return 'bx bx-money text-warning';
      }
      return 'bx bx-credit-card text-info';
    },

    addBeneficiary() {
      if (this.isMemberDeleted) {
        this.showAlert('failed', 'Cannot add beneficiaries to deleted member records');
        return;
      }
      if (this.isMemberSuspended) {
        this.showAlert('failed', 'Cannot add beneficiaries to suspended member records');
        return;
      }
      this.$refs.beneficiaryModal.openAddModal();
    },

    editBeneficiary(beneficiary) {
      if (this.isMemberDeleted) {
        this.showAlert('failed', 'Cannot edit beneficiaries of deleted member records');
        return;
      }
      if (this.isMemberSuspended) {
        this.showAlert('failed', 'Cannot edit beneficiaries of suspended member records');
        return;
      }
      this.$refs.beneficiaryModal.openEditModal(beneficiary);
    },

    removeBeneficiary(beneficiary) {
      if (this.isMemberDeleted) {
        this.showAlert('failed', 'Cannot remove beneficiaries from deleted member records');
        return;
      }
      if (this.isMemberSuspended) {
        this.showAlert('failed', 'Cannot remove beneficiaries from suspended member records');
        return;
      }
      this.$refs.beneficiaryModal.openDeleteModal(beneficiary);
    },

    handleBeneficiarySaved(event) {
      this.showAlert('success', `Beneficiary ${event.action} successfully`);
      this.loadMemberDetails();
    },

    handleBeneficiaryRemoved(event) {
      this.showAlert('success', 'Beneficiary removed successfully');
      this.loadMemberDetails();
    },

    recordPayment() {
      if (this.isMemberDeleted) {
        this.showAlert('failed', 'Cannot record payments for deleted member records');
        return;
      }

      if (this.isMemberSuspended) {
        this.showAlert('failed', 'Cannot record payments for suspended member records');
        return;
      }

      const balanceDue = this.remainingBalance;

      this.$refs.paymentModal.openModal({
        balanceDue: balanceDue,
        totalPaidPremiums: this.totalPaidThisYear,
        selectedPlan: this.selectedPlan,
        annualPremium: this.annualPremium
      });
    },

    handlePaymentRecorded(event) {
      this.showAlert('success', event.message);
      this.loadMemberDetails();
    },

    handlePaymentError(event) {
      this.showAlert('failed', event.message);
    },

    handleViewReceipt(event) {
      this.$refs.receiptModal.openModal(event.payment);
    },

    viewReceipt(payment) {
      this.$refs.receiptModal.openModal(payment);
    },

    downloadReceipt(payment) {
      this.$refs.receiptModal.openModal(payment);
      setTimeout(() => {
        this.$refs.receiptModal.downloadReceipt();
      }, 1000);
    },

    viewClaimDetails(claim) {
      this.showAlert('info', `View details for claim ${claim.claim_number}`);
    },

    downloadClaimReceipt(claim) {
      this.showAlert('info', `Download receipt for claim ${claim.claim_number}`);
    },

    viewPhotoFullScreen() {
      const photoUrl = this.getPhotoUrl();
      this.$refs.photoViewModal.openModal(photoUrl);
    },

    handlePhotoDownloaded(event) {
      this.showAlert('success', `Photo downloaded successfully as ${event.filename}`);
    },

    handleDownloadError(event) {
      this.showAlert('failed', `Error downloading photo: ${event.error}`);
    },

    handleUrlCopied(event) {
      this.showAlert('info', 'Photo URL copied to clipboard');
    },

    handleCopyError(event) {
      this.showAlert('failed', 'Failed to copy URL to clipboard');
    },

    openPhotoModal(event) {
      if (event) {
        event.stopPropagation();
      }

      if (this.isMemberDeleted) {
        this.showAlert('failed', 'Cannot update photos for deleted member records');
        return;
      }
      if (this.isMemberSuspended) {
        this.showAlert('failed', 'Cannot update photos for suspended member records');
        return;
      }
      this.$refs.memberPhotoModal.openModal();
    },

    getCardButtonTooltip() {
      if (this.isMemberDeleted) return 'Cannot view card for deleted member';
      if (this.isMemberSuspended) return 'Cannot view card for suspended member';
      if (this.isCardIssued) return 'View and reprint CBHI card';
      return 'Issue CBHI card to member';
    }
  }
};
</script>

<style scoped>
/* =====================================================
   PLAN CHANGE MODAL STYLES
   ===================================================== */

/* Modal Enhancements */
.modal-dialog {
  max-width: 1200px;
}

.modal-header.bg-primary {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%) !important;
}

.modal-body {
  max-height: 70vh;
  overflow-y: auto;
}

.modal-footer.bg-light {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
}

/* Plan Selection Cards */
.plan-selection-card {
  transition: all 0.3s ease;
  cursor: pointer;
  border-width: 2px;
}

.plan-selection-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.plan-selection-card.selected-plan {
  border-color: #2c5aa0 !important;
  box-shadow: 0 0 0 0.2rem rgba(44, 90, 160, 0.25);
}

.plan-selection-card.current-plan-card {
  border-color: #6c757d !important;
  box-shadow: 0 0 0 0.2rem rgba(108, 117, 125, 0.25);
}

/* Card Header Styles */
.plan-selection-card .card-header.bg-primary {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%) !important;
}

.plan-selection-card .card-header.bg-secondary {
  background: linear-gradient(135deg, #6c757d 0%, #8d969f 100%) !important;
}

.plan-selection-card .card-header.bg-light {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
}

/* Pricing Display */
.pricing-display {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
}

.pricing-display:hover {
  background: linear-gradient(135deg, #e3f2fd 0%, #f1f8ff 100%);
  border-color: #2c5aa0;
}

/* Coverage Summary */
.coverage-summary {
  background-color: #fefefe;
  border-radius: 0.375rem;
  padding: 0.75rem;
  border: 1px solid #f1f3f4;
}

.coverage-item {
  display: flex;
  align-items: center;
  padding: 0.25rem 0;
}

.coverage-item i {
  flex-shrink: 0;
  font-size: 1rem;
}

/* Premium Calculation */
.premium-calculation {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 0.5rem;
  border: 1px solid #dee2e6;
  transition: all 0.3s ease;
}

.premium-calculation:hover {
  background: linear-gradient(135deg, #e3f2fd 0%, #f1f8ff 100%);
  border-color: #2c5aa0;
}

.premium-breakdown {
  font-size: 0.875rem;
}

.premium-breakdown .small {
  line-height: 1.2;
  margin-bottom: 0.125rem;
}

/* Plan Comparison */
.comparison-item {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid #e9ecef;
  height: 100%;
}

.premium-change,
.coverage-changes,
.services-impact {
  font-size: 0.875rem;
}

.change-item {
  background-color: #fefefe;
  padding: 0.5rem;
  border-radius: 0.25rem;
  border-left: 3px solid transparent;
  transition: all 0.2s ease;
}

.change-item:hover {
  border-left-color: #2c5aa0;
  background: linear-gradient(135deg, #e3f2fd 0%, #f1f8ff 100%);
}

.impact-item {
  padding: 0.25rem 0;
}

/* Service Comparison */
.service-comparison {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 0.5rem;
  padding: 1rem;
}

.service-list {
  max-height: 150px;
  overflow-y: auto;
}

.services-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.services-grid .badge {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.services-grid .badge:hover {
  transform: scale(1.05);
}

/* =====================================================
   BADGE STYLES
   ===================================================== */

.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  font-weight: 500;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.badge.bg-info {
  background-color: #0dcaf0 !important;
  color: #000 !important;
}

.badge.bg-primary {
  background-color: #2c5aa0 !important;
  color: white !important;
}

.badge.bg-success {
  background-color: #198754 !important;
  color: white !important;
}

.badge.bg-warning {
  background-color: #ffc107 !important;
  color: #000 !important;
}

.badge.bg-secondary {
  background-color: #6c757d !important;
  color: white !important;
}

.badge.bg-light {
  background-color: #f8f9fa !important;
  color: #495057 !important;
  border: 1px solid #dee2e6;
}

.badge.bg-danger {
  background-color: #dc3545 !important;
  color: white !important;
}

/* ✅ NEW: Purple badge for dependency */
.badge.bg-purple {
  background-color: #6f42c1 !important;
  color: white !important;
}

.text-purple {
  color: #6f42c1 !important;
}

.bg-purple {
  background-color: #6f42c1 !important;
}

/* ✅ NEW: Badge opacity variants */
.badge.bg-danger.bg-opacity-15 {
  background-color: rgba(220, 53, 69, 0.15) !important;
}

.badge.bg-purple.bg-opacity-15 {
  background-color: rgba(111, 66, 193, 0.15) !important;
}

.badge.bg-info.bg-opacity-10 {
  background-color: rgba(13, 202, 240, 0.1) !important;
}

.badge.bg-success.bg-opacity-10 {
  background-color: rgba(25, 135, 84, 0.1) !important;
}

.badge.bg-warning.bg-opacity-10 {
  background-color: rgba(255, 193, 7, 0.1) !important;
}

.badge.bg-primary.bg-opacity-10 {
  background-color: rgba(44, 90, 160, 0.1) !important;
}

.badge.bg-purple.bg-opacity-10 {
  background-color: rgba(111, 66, 193, 0.1) !important;
}

/* =====================================================
   ALERT STYLES
   ===================================================== */

.alert {
  border-radius: 0.5rem;
  border-width: 1px;
  border-left-width: 4px;
}

.alert-success {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border-left-color: #28a745;
}

.alert-danger {
  background: linear-gradient(135deg, #f8d7da 0%, #f1b0b7 100%);
  border-left-color: #dc3545;
}

.alert-info {
  background: linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%);
  border-left-color: #2c5aa0;
}

.alert-warning {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border-left-color: #ffc107;
}

/* =====================================================
   FORM CONTROLS
   ===================================================== */

.form-control {
  border-radius: 0.5rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #2c5aa0;
  box-shadow: 0 0 0 0.2rem rgba(44, 90, 160, 0.25);
}

/* =====================================================
   BUTTON STYLES
   ===================================================== */

.btn {
  border-radius: 0.5rem;
  font-weight: 500;
  transition: all 0.3s ease;
  padding: 0.5rem 1rem;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn-primary {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  border-color: #2c5aa0;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #1e3f70 0%, #357abd 100%);
  border-color: #1e3f70;
}

.btn-secondary {
  background: linear-gradient(135deg, #6c757d 0%, #8d969f 100%);
  border-color: #6c757d;
}

.btn-secondary:hover {
  background: linear-gradient(135deg, #545b62 0%, #6d7580 100%);
  border-color: #545b62;
}

.btn-outline-info {
  color: #17a2b8;
  border-color: #17a2b8;
}

.btn-outline-info:hover {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* =====================================================
   SPINNER
   ===================================================== */

.spinner-border {
  width: 2rem;
  height: 2rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* =====================================================
   TEXT UTILITIES
   ===================================================== */

.text-white-75 {
  color: rgba(255, 255, 255, 0.75) !important;
}

.text-primary {
  color: #2c5aa0 !important;
}

.text-success {
  color: #198754 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-info {
  color: #17a2b8 !important;
}

/* =====================================================
   CLOSE BUTTON
   ===================================================== */

.btn-close-white {
  filter: invert(1) grayscale(100%) brightness(200%);
}

/* =====================================================
   SCROLLBAR STYLING
   ===================================================== */

.modal-body::-webkit-scrollbar,
.service-list::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track,
.service-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb,
.service-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
  transition: background 0.3s ease;
}

.modal-body::-webkit-scrollbar-thumb:hover,
.service-list::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* =====================================================
   ANIMATIONS
   ===================================================== */

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.plan-selection-card {
  animation: slideIn 0.3s ease-out;
}

.comparison-item {
  animation: slideIn 0.4s ease-out;
}

/* =====================================================
   PHOTO INTERACTION STYLES
   ===================================================== */

.photo-clickable {
  transition: all 0.3s ease;
  position: relative;
}

.photo-clickable:hover {
  transform: scale(1.05);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
  filter: brightness(1.1);
}

.photo-clickable:active {
  transform: scale(0.98);
}

.photo-view-indicator {
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.photo-clickable:hover + .photo-view-indicator,
.position-relative:hover .photo-view-indicator {
  opacity: 1;
}

.photo-update-btn {
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.photo-update-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.bg-primary.photo-clickable:hover {
  background: linear-gradient(135deg, #4a90e2 0%, #2c5aa0 100%) !important;
  transform: scale(1.05);
}

.photo-clickable:focus {
  outline: 3px solid #4a90e2;
  outline-offset: 2px;
}

/* =====================================================
   BENEFICIARY PHOTO STYLES
   ===================================================== */

.member-photo-btn,
.beneficiary-photo-btn {
  transition: all 0.2s ease;
  opacity: 0;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

tr:hover .member-photo-btn,
tr:hover .beneficiary-photo-btn,
.position-relative:hover .member-photo-btn,
.position-relative:hover .beneficiary-photo-btn {
  opacity: 1;
}

.member-photo-btn:hover,
.beneficiary-photo-btn:hover {
  transform: scale(1.15);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
}

.bg-soft-secondary {
  background-color: #f3f6f9 !important;
}

.avatar-title {
  font-weight: 500;
  color: #ffffff;
  font-size: 14px;
}

/* =====================================================
   TABLE STYLING
   ===================================================== */

.contacts-table tbody tr {
  transition: all 0.2s ease;
}

.contacts-table tbody tr:hover {
  background-color: #f8f9fa;
}

/* Photo status indicators */
.badge.rounded-pill {
  min-width: 16px;
  min-height: 16px;
}

/* =====================================================
   SPECIAL STATUS COLUMN STYLES
   ===================================================== */

/* ✅ NEW: Special status badges */
.badge[data-bs-toggle="tooltip"] {
  cursor: help;
}

/* Disability badge styling */
.badge.bg-danger.bg-opacity-15.text-danger {
  background-color: rgba(220, 53, 69, 0.15) !important;
  color: #dc3545 !important;
  border: 1px solid rgba(220, 53, 69, 0.3);
}

.badge.bg-danger.bg-opacity-15.text-danger:hover {
  background-color: rgba(220, 53, 69, 0.25) !important;
}

/* Dependency badge styling */
.badge.bg-purple.bg-opacity-15.text-purple {
  background-color: rgba(111, 66, 193, 0.15) !important;
  color: #6f42c1 !important;
  border: 1px solid rgba(111, 66, 193, 0.3);
}

.badge.bg-purple.bg-opacity-15.text-purple:hover {
  background-color: rgba(111, 66, 193, 0.25) !important;
}

/* Special status column flex layout */
td .d-flex.flex-column.gap-1 {
  min-width: 100px;
}

td .d-flex.flex-column.gap-1 .badge {
  font-size: 0.7rem;
  padding: 0.2rem 0.4rem;
  white-space: nowrap;
}

/* =====================================================
   COST COLUMN BADGES
   ===================================================== */

/* Free badges in cost column */
td .badge.bg-success,
td .badge.bg-info,
td .badge.bg-purple {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
}

td .badge.bg-success i,
td .badge.bg-info i,
td .badge.bg-purple i {
  font-size: 0.65rem;
}

/* =====================================================
   BENEFICIARY BREAKDOWN STYLES
   ===================================================== */

/* Footer breakdown badges */
tfoot .d-flex.flex-wrap.gap-2 .badge {
  font-size: 0.75rem;
  padding: 0.35rem 0.6rem;
}

tfoot .d-flex.flex-wrap.gap-2 .badge i {
  font-size: 0.7rem;
}

/* =====================================================
   PLAN RULES SUMMARY ALERT
   ===================================================== */

.alert.alert-info.py-2.mb-3 {
  background: linear-gradient(135deg, #e3f2fd 0%, #f1f8ff 100%);
  border-color: #2c5aa0;
  border-left-width: 4px;
}

.alert.alert-info.py-2.mb-3 .badge {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
}

/* =====================================================
   RESPONSIVE DESIGN
   ===================================================== */

@media (max-width: 1200px) {
  .modal-dialog {
    max-width: 95%;
    margin: 1rem;
  }
}

@media (max-width: 992px) {
  .modal-dialog {
    max-width: 90%;
  }

  .premium-calculation .d-flex {
    flex-direction: column;
    text-align: center;
  }

  .premium-calculation .text-end {
    text-align: center !important;
    margin-top: 1rem;
  }

  .change-item .d-flex {
    flex-direction: column;
    text-align: center;
    gap: 0.25rem;
  }
}

@media (max-width: 768px) {
  .modal-body {
    max-height: 60vh;
    padding: 1rem;
  }

  .plan-selection-card {
    margin-bottom: 1rem;
  }

  .pricing-display {
    padding: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .coverage-summary,
  .premium-calculation {
    padding: 0.5rem;
  }

  .comparison-item {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }

  .modal-footer {
    flex-direction: column;
    gap: 1rem;
  }

  .modal-footer .d-flex {
    flex-direction: column;
    align-items: stretch !important;
    gap: 0.5rem;
  }

  .services-grid {
    justify-content: center;
  }

  /* Responsive photo buttons */
  .photo-clickable {
    width: 70px !important;
    height: 70px !important;
  }

  .photo-update-btn {
    width: 28px !important;
    height: 28px !important;
    bottom: -3px !important;
    right: -3px !important;
  }

  .beneficiary-photo-btn,
  .member-photo-btn {
    opacity: 1;
  }

  .table-responsive {
    font-size: 0.875rem;
  }

  .btn-group .btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }

  /* ✅ NEW: Responsive special status column */
  td .d-flex.flex-column.gap-1 {
    min-width: 80px;
  }

  td .d-flex.flex-column.gap-1 .badge {
    font-size: 0.6rem;
    padding: 0.15rem 0.3rem;
  }

  /* Hide some text on small screens */
  td .badge.bg-danger.bg-opacity-15.text-danger span,
  td .badge.bg-purple.bg-opacity-15.text-purple span {
    display: none;
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    margin: 0.5rem;
    max-width: 95%;
  }

  .card-header .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .card-header .d-flex > div:last-child {
    margin-top: 0.5rem;
    align-self: flex-end;
  }

  .coverage-item {
    font-size: 0.8rem;
  }

  .badge {
    font-size: 0.7rem;
    padding: 0.15rem 0.3rem;
  }

  .premium-breakdown {
    font-size: 0.8rem;
  }

  .btn {
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
  }

  /* ✅ NEW: Hide special status text on very small screens, show only icons */
  td .d-flex.flex-column.gap-1 .badge {
    padding: 0.2rem;
  }

  td .d-flex.flex-column.gap-1 .badge i {
    margin-right: 0 !important;
  }

  /* Plan rules summary responsive */
  .alert.alert-info.py-2.mb-3 .d-flex.flex-wrap {
    flex-direction: column;
    align-items: flex-start;
  }

  .alert.alert-info.py-2.mb-3 small {
    margin-bottom: 0.25rem;
  }
}

/* =====================================================
   PRINT STYLES
   ===================================================== */

@media print {
  .modal {
    position: static !important;
    display: block !important;
  }

  .modal-dialog {
    max-width: none !important;
    margin: 0 !important;
  }

  .modal-header,
  .modal-footer,
  .btn {
    display: none !important;
  }

  .plan-selection-card {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /* Print special status badges */
  td .d-flex.flex-column.gap-1 .badge {
    border: 1px solid #000 !important;
    background: transparent !important;
    color: #000 !important;
  }
}

/* =====================================================
   HIGH CONTRAST MODE
   ===================================================== */

@media (prefers-contrast: high) {
  .plan-selection-card,
  .comparison-item,
  .pricing-display,
  .premium-calculation {
    border-width: 2px;
  }

  .badge {
    border: 1px solid;
  }

  .btn {
    border-width: 2px;
  }

  /* High contrast special status badges */
  .badge.bg-danger.bg-opacity-15.text-danger,
  .badge.bg-purple.bg-opacity-15.text-purple {
    border-width: 2px;
  }
}

/* =====================================================
   REDUCED MOTION
   ===================================================== */

@media (prefers-reduced-motion: reduce) {
  .plan-selection-card,
  .comparison-item,
  .btn,
  .badge,
  .pricing-display,
  .premium-calculation {
    transition: none;
  }

  .plan-selection-card:hover,
  .btn:hover {
    transform: none;
  }

  .photo-clickable,
  .photo-update-btn,
  .member-photo-btn,
  .beneficiary-photo-btn {
    transition: none;
  }

  .photo-clickable:hover {
    transform: none;
  }
}

/* =====================================================
   ADDITIONAL UTILITY CLASSES
   ===================================================== */

/* Avatar styles */
.avatar-sm {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-sm .avatar-title {
  font-size: 12px;
}

/* Soft background variants */
.bg-soft-info {
  background-color: rgba(13, 202, 240, 0.1) !important;
}

.bg-soft-success {
  background-color: rgba(25, 135, 84, 0.1) !important;
}

.bg-soft-warning {
  background-color: rgba(255, 193, 7, 0.1) !important;
}

.bg-soft-danger {
  background-color: rgba(220, 53, 69, 0.1) !important;
}

.bg-soft-purple {
  background-color: rgba(111, 66, 193, 0.1) !important;
}

/* Card transitions */
.card {
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-2px);
}

/* Service card styles */
.service-card {
  transition: all 0.2s ease;
}

.service-card:hover {
  background-color: #e9ecef !important;
  transform: translateX(5px);
}

/* Waiting period card */
.waiting-period-card {
  transition: all 0.2s ease;
}

.waiting-period-card:hover {
  background-color: #e9ecef !important;
}

/* Rule items */
.rule-item {
  padding: 0.5rem;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.rule-item:hover {
  background-color: #f8f9fa;
}

/* Premium summary box */
.premium-summary {
  border: 2px dashed #dee2e6;
  transition: all 0.3s ease;
}

.premium-summary:hover {
  border-color: #2c5aa0;
  background-color: #e3f2fd !important;
}

/* Category title */
.category-title {
  border-bottom: 2px solid #2c5aa0;
  padding-bottom: 0.5rem;
}

/* Table footer styling */
tfoot.table-light tr td {
  background-color: #f8f9fa;
  border-top: 2px solid #dee2e6;
}

tfoot.table-light tr:first-child td {
  padding-top: 1rem;
}

tfoot.table-light tr:last-child td {
  padding-bottom: 1rem;
}

/* Tooltip enhancements for special status */
[data-bs-toggle="tooltip"] {
  cursor: help;
}

/* =====================================================
   CUSTOM NAV TABS
   ===================================================== */

.custom-nav-tabs {
  border-bottom: 2px solid #dee2e6;
}

.custom-nav-tabs .nav-link {
  border: none;
  border-bottom: 3px solid transparent;
  color: #6c757d;
  font-weight: 500;
  padding: 1rem 1.5rem;
  transition: all 0.3s ease;
}

.custom-nav-tabs .nav-link:hover {
  border-bottom-color: #dee2e6;
  color: #2c5aa0;
}

.custom-nav-tabs .nav-link.active {
  border-bottom-color: #2c5aa0;
  color: #2c5aa0;
  background-color: transparent;
}

/* =====================================================
   PLAN INFO SECTION
   ===================================================== */

.plan-info-section {
  padding: 0.75rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 0.5rem;
  border-left: 4px solid #2c5aa0;
}

/* =====================================================
   MISCELLANEOUS
   ===================================================== */

/* Ensure proper z-index for dropdowns and tooltips */
.dropdown-menu {
  z-index: 1050;
}

.tooltip {
  z-index: 1080;
}

/* Fix for button groups */
.btn-group > .btn:not(:last-child) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.btn-group > .btn:not(:first-child) {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

/* Ensure consistent icon sizing in badges */
.badge i {
  vertical-align: middle;
}

/* Text truncation */
.text-truncate-1-line {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Min height for cards */
.min-h-90vh-xl {
  min-height: auto;
}

@media (min-width: 1200px) {
  .min-h-90vh-xl {
    min-height: 90vh;
  }
}
</style>